/**
 * crm-engine fetch 客户端（admin web SPA → /api/v1/crm/* via vite proxy / nginx）。
 *
 * - 走相对路径，让 vite dev proxy (3110) 或 prod nginx 反代统一处理跨进程。
 * - 默认带 credentials: 'include' 让 auth cookie 跟随。
 * - 错误约定：HTTP !2xx 抛 CrmApiError；调用方用 react-query 的 error state 渲染。
 * - x-crm-module + x-crm-api-key header 留空 — admin 在主 nginx 已加 'admin' 模块鉴权（部署侧）。
 *   测试 / dev 模式下，crm-engine 默认 keys 缺时鉴权关闭（middleware loadCrmApiKeysFromEnv 行为）。
 */

export interface CursorListResponse<T> {
  ok: true;
  entries: T[];
  next_before: string | null;
}

/** catalog 用 skip/limit/total 分页（非 cursor），与 mongo $skip 对齐。 */
export interface SkipListResponse<T> {
  ok: true;
  entries: T[];
  total: number;
  limit: number;
  skip: number;
}

export interface SupplierContact {
  name: string | null;
  role: string | null;
  phone: string | null;
  wechat: string | null;
  email: string | null;
}

/** 采购询价（10-23）：业务员 → 飞书审批 → 采购员在审批里填报价 */
export interface PurchaseInquiryEntry {
  _id: string;
  inquiry_no: string;
  customer_id: string;
  customer_alias: string | null;
  status: 'pending' | 'accepted' | 'replied' | 'quoted' | 'rejected' | 'canceled';
  reject_reason: string | null;
  purchase_note: string | null;
  lines: Array<{
    title: string | null;
    link: string | null;
    spec: string | null;
    quantity: number | null;
    target_price: number | null;
  }>;
  replies: Array<{
    title: string | null;
    supplier_id: string | null;
    supplier_name: string | null;
    cost_cny: number | null;
    moq: number | null;
    lead_days: number | null;
    note: string | null;
  }>;
  created_at: string;
  replied_at: string | null;
}

export interface PurchaseInquiryInput {
  lines: Array<{
    title: string;
    link?: string | null;
    spec?: string | null;
    quantity?: number | null;
    target_price?: number | null;
  }>;
  expected_delivery_at?: string | null;
}

/** 采购单（10-23）：一家供应商一张，进度走 purchase_default 流程 */
export interface PurchaseOrderEntry {
  _id: string;
  po_number: string;
  order_id: string | null;
  supplier_id: string;
  supplier_name?: string | null;
  owner_name?: string | null;
  items: Array<{
    factory_sku: string | null;
    title: string | null;
    quantity: number;
    cost_cny?: number | null;
  }>;
  total_cny?: number | null;
  flow_key: string;
  status: string;
  status_changed_at: string | null;
  due_at: string | null;
  delivered_to: string | null;
  delivered_at: string | null;
  tracking_no: string | null;
  carrier: string | null;
  notes?: string | null;
  created_at: string;
}

export interface FeishuApprovalEntry {
  _id: string;
  instance_code: string;
  kind: 'purchase_inquiry' | 'purchase_order' | 'offline_payment' | 'supplier_delete' | null;
  is_test: boolean;
  uuid: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELED' | 'DELETED';
  processed_status: 'pending' | 'processing' | 'done' | 'failed' | 'unlinked' | 'ignored';
  error: string | null;
  comment: string | null;
  attempts: number;
  event_count: number;
  ref_type: string | null;
  ref_id: string | null;
  result: unknown;
  created_at: string;
  processed_at: string | null;
}

/** 供应商删除审批（10-23）。审批中照常可用；通过 = 归档 + 名下产品下架 */
export interface SupplierDeleteRequest {
  status: 'pending' | 'approved' | 'rejected';
  reason: string | null;
  requested_by_name: string | null;
  requested_at: string | null;
  decided_at: string | null;
  comment: string | null;
}

export interface SupplierDeleteImpact {
  product_count: number;
  listed_count: number;
  bound_tenant: string | null;
  open_purchase_orders: Array<{ po_number: string; status: string }>;
}

export interface SupplierEntry {
  _id: string;
  delete_request?: SupplierDeleteRequest | null;
  supplier_code: string;
  name: string;
  short_name: string | null;
  category: string[];
  address?: { province?: string | null; city?: string | null; detail?: string | null };
  contacts?: SupplierContact[];
  capacity_monthly_pcs: number | null;
  min_order_qty: number | null;
  payment_terms?: string | null;
  lead_time_days: number | null;
  export_capable: boolean | null;
  url_1688: string | null;
  url_alibaba: string | null;
  source: string;
  initial_score: number | null;
  current_score: number | null;
  /** 系统状态:决定参不参与撮合。与合作状态是两回事。 */
  status: string;
  /** 业务进度:潜在/已建联/已打样/已合作/暂停/拉黑 */
  cooperation_status?: string | null;
  /** 合作等级:战略/主力/备选/试单 */
  cooperation_level?: string | null;
  notes?: string | null;
  /** 归属产品线:link4a=云帆 / strand=Strand;空 = 待开发 */
  product_lines?: string[];
  product_lines_manual?: boolean;
  /** 这家就是建站客户(货是客户自己的),资料从这个站的建站资料带过来 */
  site_customer?: { slug: string; domain: string | null; synced_at: string | null } | null;
  /** ── 飞书供应商台账镜像(只读,业务员在飞书那张表里改)── */
  supplier_type?: string | null;
  /** 飞书在用的 A/B/C 等级,与 cooperation_level 是两套口径 */
  grade?: string | null;
  price_tiers?: string[];
  region?: string | null;
  main_materials?: string | null;
  main_style_note?: string | null;
  in_stock_ready?: boolean | null;
  invoice_capable?: boolean | null;
  tax_rate?: string | null;
  settlement_terms?: string | null;
  feishu_status_raw?: string | null;
  feishu_record_id?: string | null;
  feishu_supplier_code?: string | null;
  feishu_synced_at?: string | null;
  /** ── 第二阶段深度档案(深度合作后业务员在飞书补)── */
  profile?: {
    equipment_count?: number | null;
    staff_count?: number | null;
    scale_tier?: string | null;
    customer_segments?: string[];
    style_scenes?: string[];
    research_notes?: string | null;
  } | null;
  created_at: string;
  updated_at: string;
}

/** 搜索框的自然语言解析结果 */
export interface ParsedSearch {
  kind: 'filter' | 'question';
  scope: 'suppliers' | 'products';
  filters: Record<string, string>;
  keyword: string | null;
  explain: string;
  /** true = 没解析出条件,已退回纯关键词搜索 */
  fallback: boolean;
  /** 像条件、但业务词典里没有的词 —— 没算进条件,要告诉业务员 */
  unknown_terms?: string[];
}

/** 同步的一轮运行记录:进度 + 日志 + 结果 */
export interface SupplierSyncRunEntry {
  _id: string;
  trigger: 'manual' | 'scheduled';
  status: 'running' | 'success' | 'failed';
  progress: { phase: string; label: string; done: number; total: number; percent: number };
  logs?: Array<{ at: string; level: 'info' | 'warn' | 'error'; msg: string }>;
  summary: SupplierSyncSummary | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
}

/** 飞书台账同步一轮的结果(含"待人工处理"三类) */
export interface SupplierSyncSummary {
  synced_at: string;
  feishu_rows: number;
  created: number;
  updated: number;
  linked: Array<{ supplier_code: string; local_name: string; feishu_name: string }>;
  skipped_no_name: number;
  duplicates: Array<{ name: string; kept: string; skipped: string[] }>;
  unmatched_local: Array<{ supplier_code: string; name: string }>;
  ambiguous: Array<{ local_name: string; candidates: string[] }>;
}

export interface SupplierHealthIssue {
  code: string;
  severity: 'red' | 'amber' | 'gray';
  count: number;
  detail: string | null;
}

/** 周检产出的单家体检单 */
export interface SupplierHealthEntry {
  _id: string;
  supplier_id: string;
  supplier_code: string;
  supplier_name: string | null;
  issues: SupplierHealthIssue[];
  worst_severity: 'red' | 'amber' | 'gray' | null;
  issue_count: number;
  stats: {
    product_count: number;
    selling_count: number;
    priced_count: number;
    stale_price_count: number;
    oldest_price_age_days: number | null;
    avg_price_cny: number | null;
    top_categories: string[];
  };
  checked_at: string;
}

/** 供应商档案里业务员可手工维护的字段(与后端 SupplierPatchBody 对齐) */
export interface SupplierPatch {
  name?: string;
  short_name?: string | null;
  city?: string | null;
  province?: string | null;
  address_detail?: string | null;
  category?: string[];
  contacts?: SupplierContact[];
  capacity_monthly_pcs?: number | null;
  min_order_qty?: number | null;
  payment_terms?: string | null;
  lead_time_days?: number | null;
  export_capable?: boolean | null;
  url_1688?: string | null;
  url_alibaba?: string | null;
  cooperation_status?: string;
  cooperation_level?: string | null;
  notes?: string | null;
  product_lines?: string[];
}

export interface ProductImageRef {
  type: 'dispimg' | 'url';
  ref: string;
}

export interface ProductEntry {
  _id: string;
  supplier_id: string;
  /** 系统级全局唯一 SKU ID,格式 `<supplier_code>/<factory_sku>`(2026-05-30 加入) */
  global_sku: string;
  factory_sku: string;
  title: string | null;
  category: string | null;
  sub_category: string | null;
  price_cny: number | null;
  price_raw: string | null;
  /** 这个成本价是哪天的;null = 老数据,不知道哪天的价 */
  price_quoted_at?: string | null;
  moq: number | null;
  url_1688: string | null;
  /** 报价单规格列(2026-09-03);对应站点 SKU 表详情页规格区 */
  material?: string | null;
  plating?: string | null;
  stone?: string | null;
  color?: string | null;
  dimensions?: string | null;
  weight_g?: number | null;
  lead_time_days?: number | null;
  packaging?: string | null;
  customization?: string | null;
  hs_code?: string | null;
  image_refs?: ProductImageRef[];
  status: string;
  /**
   * 人工指定的站上排序权重(`products.manual_sort_weight`)。空 = 没人定过，按规则算。
   *
   * 有值的时候界面上要说清楚是**人工定的** —— 否则业务员看见「靠后」会去找供应商
   * 改品名，而实际上是他自己（或同事）上周按的按钮。
   */
  manual_sort_weight?: number | null;
  /** 人工定的售价(元)。空 = 没人定过，按成本 × 站级倍率 */
  sell_price_cny?: number | null;
  /** 售价 / 增价比(后端按站上同一算法算好)。工厂端不返回 */
  sell?: ProductSellPrice;
  /**
   * 这条货在各个开了货盘的站上可不可见、不可见是因为什么。
   * 来自 `catalog_items` 投影 —— 站上看不看得见的判据在那儿，不在 `status` 上。
   */
  catalog?: CatalogSiteState[];
  /** 一品多站上架关系:该产品已上架到哪些盒子站(上架时回写,飞书删行+更新网站后回收) */
  published_sites?: PublishedSiteEntry[];
  created_at: string;
  updated_at?: string;
}

export interface ProductSellPrice {
  /** 按哪个货盘站的倍率算的 */
  site: string;
  /** 站级倍率，如 1.2 */
  site_markup: number;
  /** 人民币售价；成本和人工售价都没有时为空 */
  price_cny: number | null;
  /** true = 业务手填的售价；false = 成本 × 站级倍率 */
  manual: boolean;
  /** 增价比 =(售价 − 成本)÷ 成本，0.2 = 加价 20%；没成本时为空 */
  markup_ratio: number | null;
}

export interface CatalogSiteState {
  slug: string;
  visible: boolean;
  hidden_reason: string | null;
  /**
   * 站上排序权重，数大的排前面，默认 10。来自 `catalog_items.sort_weight`。
   *
   * **最终生效值**：人工定过(`ProductEntry.manual_sort_weight`)就是人工那个数，
   * 没人定过才按规则算 —— 品名是材质清单的 9，其余 10，供应商补了正经品名自动回到 10。
   */
  sort_weight?: number;
  /** 是不是这个站的首页精选（`Project.catalog.featured_skus` 里有它） */
  featured?: boolean;
}

export interface PublishedSiteEntry {
  site: string;
  sku_code: string;
  markup?: number | null;
  status?: string | null;
  published_at?: string;
}

/** 员工手动编辑 SKU 时可改的字段(白名单);后端走"有意识覆盖"语义,允许写入 null 显式清空 */
export interface ProductPatch {
  title?: string | null;
  category?: string | null;
  sub_category?: string | null;
  price_cny?: number | null;
  sell_price_cny?: number | null;
  price_raw?: string | null;
  moq?: number | null;
  url_1688?: string | null;
  material?: string | null;
  plating?: string | null;
  stone?: string | null;
  color?: string | null;
  dimensions?: string | null;
  weight_g?: number | null;
  lead_time_days?: number | null;
  packaging?: string | null;
  customization?: string | null;
  hs_code?: string | null;
  image_refs?: ProductImageRef[];
}

export interface CraftEntry {
  _id: string;
  name: string;
  aliases: string[];
  parent_id: string | null;
  description: string | null;
  supplier_count: number;
  product_count: number;
  source: string;
  status: string;
}

export interface CatalogSummary {
  ok: true;
  counts: { suppliers: number; products: number; crafts: number };
}

export interface DashboardSummary {
  ok: true;
  counts: {
    suppliers: number;
    products: number;
    crafts: number;
    customers: number | null;
    customer_events: number | null;
    drafts: number | null;
    push_events: number | null;
    acked: number | null;
  };
  ack_rate: number | null;
}

export interface CrmApiErrorBody {
  ok: false;
  error: string;
  issues?: unknown;
}

/** 已上传到私有存储、等着发出去的文件 */
export interface OutboundFileAttachment {
  storage_key: string;
  filename: string;
  mime: string;
  size: number;
}

/** 出站文件上限，跟后端上传口一致 */
export const OUTBOUND_FILE_MAX_BYTES = 25 * 1024 * 1024;

/**
 * 直传文件（请求体就是文件本身）。用 XHR 不用 fetch：fetch 拿不到上传进度，
 * 而一个 20 MB 的 PDF 在工厂的网络上要传好几秒，没有进度条人会以为卡死了重复点。
 */
export function uploadOutboundFile(
  customerId: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
  /** 'email'：官方号过了 24 小时改发邮件，文件当邮件附件发 */
  via?: 'email' | null,
): Promise<OutboundFileAttachment> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const qs = new URLSearchParams({ customer_id: customerId, filename: file.name });
    if (via === 'email') qs.set('via', 'email');
    xhr.open('POST', `/api/v1/proactive-send/file?${qs.toString()}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('X-Crm-Module', 'admin');
    for (const [k, v] of Object.entries(actAsHeaders())) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = xhr.responseText;
      }
      const b = body as { ok?: boolean; attachment?: OutboundFileAttachment; message?: string };
      if (xhr.status >= 200 && xhr.status < 300 && b?.ok && b.attachment) {
        resolve(b.attachment);
        return;
      }
      const message =
        xhr.status === 413
          ? '文件超过 25 MB'
          : (b?.message ?? `上传失败（${xhr.status || '网络错误'}）`);
      reject(new CrmApiError(xhr.status, body, message));
    };
    xhr.onerror = () => reject(new CrmApiError(0, null, '网络断了，没传上去'));
    xhr.onabort = () => reject(new CrmApiError(0, null, '已取消'));
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });
}

export class CrmApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.name = 'CrmApiError';
    this.status = status;
    this.body = body;
  }
}

interface CrmFetchOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * **传对象，不要自己 stringify** —— 下面 crmFetch 里会做。
   *
   * 类型写成 `object | null` 而不是 `unknown`，是为了让
   * `body: JSON.stringify(x)` 变成一个编译错误：string 不是 object。
   *
   * 2026-09-22 之前这里是 `unknown`，于是 `waEgressAssign` 和 `assignCustomer`
   * 两处在外面又 stringify 了一次 —— 服务端收到的是一个 JSON 字符串而不是对象，
   * 字段全读不到，表现成 400 missing_tenant。类型、lint、后端全量测试**全部放行**：
   * 后端测试根本不加载这个文件，而 `unknown` 接受一切。
   * 这个错只在真人点那个按钮时才看得见 —— 所以把它挪进类型系统，30 秒的 typecheck
   * 每次都会拦住，不依赖「有没有人为这条路写过用例」。
   */
  body?: object | null;
  headers?: Record<string, string>;
}

/**
 * 自营站 = 一家工厂（Strand）。sitebox 里的「消息」页直接用工厂端那一屏，
 * 打开期间所有 crm 请求带上这个头，后端按自营那家工厂的身份走工厂端同一套接口。
 * 由 `SelfOperatedPortal` 挂载时打开、卸载时关掉。
 */
let actAsSelfOperated = false;
export function setActAsSelfOperated(on: boolean): void {
  actAsSelfOperated = on;
}
/** 现在是不是在自营消息页（/strand）里。采购询价 / 报价单内部栏这些只在自营那一侧出现 */
export function isActingAsSelfOperated(): boolean {
  return actAsSelfOperated;
}
/**
 * 只给内部的接口：后端见到工厂身份一律 403（assignments 路由头上那道闸）。
 * 自营消息页里的「转给工厂」是内部业务员本人在操作，不能顶着自营工厂的身份去调。
 */
const INTERNAL_ONLY_PREFIXES = ['/api/v1/crm/assignments'];
/** 带进请求头的那一项；没打开、或者是只给内部的接口，就是空对象 */
export function actAsHeaders(path = ''): Record<string, string> {
  if (!actAsSelfOperated) return {};
  if (INTERNAL_ONLY_PREFIXES.some((p) => path.startsWith(p))) return {};
  return { 'X-Genbox-As-Tenant': 'self-operated' };
}

export async function crmFetch<T>(path: string, opts: CrmFetchOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Crm-Module': 'admin',
      ...actAsHeaders(path),
      ...(opts.headers ?? {}),
    },
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(path, init);
  const text = await res.text();
  let body: unknown;
  try {
    body = text.length ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    const msg =
      typeof body === 'object' && body !== null && 'error' in body
        ? String((body as { error: unknown }).error)
        : `crm api ${path} → ${res.status}`;
    throw new CrmApiError(res.status, body, msg);
  }
  return body as T;
}

/** 入站消息表项 — 后端 CustomerEventDoc lean shape（部分字段） */
export interface InboundMessageEntry {
  _id: string;
  customer_id: string;
  event_type: string;
  channel: string;
  ts: string;
  direction: 'inbound';
  payload?: Record<string, unknown>;
  text_excerpt?: string | null;
  /** 简体中文译文（后台 haiku 异步回填；null 表示未翻 / 原文即中文） */
  text_excerpt_zh?: string | null;
  /** 被客户联系的商户号（原始 display 号码 / 收件邮箱 / 站点） */
  inbound_to?: string | null;
  /** 来源号码注册表回填的人话备注名（如「Strand 美国主号」） */
  inbound_to_label?: string | null;
  lang?: string | null;
  trace_id?: string | null;
  /** 列表接口回填的客户名/国家（并入「客户与询盘」页后业务员要认得出是谁） */
  customer_display_name?: string | null;
  customer_country?: string | null;
}

export interface AgentDecisionEntry {
  _id: string;
  customer_id: string;
  customer_event_id?: string | null;
  decision_type: string;
  module: string;
  ts: string;
  result?: Record<string, unknown>;
  latency_ms?: number | null;
  model?: string | null;
  trace_id?: string | null;
  human_review?: 'accepted' | 'edited' | 'rejected' | 'auto_applied' | null;
  human_review_at?: string | null;
  human_review_by?: string | null;
}

export interface DraftSiteEntry {
  /** 站点 slug；null = 无站点来源（存量草稿 / 非 site 渠道） */
  site_slug: string | null;
  /** 该站点待处理草稿数 */
  pending: number;
}

export interface DraftEntry {
  _id: string;
  message_id: string;
  scene: string;
  customer_id: string | null;
  trace_id: string | null;
  site_slug: string | null;
  draft_body: string | null;
  /** 简体中文大意（haiku 翻译；让业务员秒懂英文/法/西草稿） */
  draft_body_zh?: string | null;
  draft_language: string | null;
  draft_confidence: number | null;
  status: 'generated' | 'manual-review' | 'skipped';
  triggered_by: 'L1' | 'L2' | 'L3' | null;
  business_action: string | null;
  business_action_by: string | null;
  business_action_at: string | null;
  created_at: string;
}

/** 客户阶段：人定的六档，选项由 /follow-up 接口下发 */
export interface StageOption {
  key: string;
  label: string;
}

export interface FollowUpSummary {
  stage: string;
  stage_label: string;
  owner_user_id: string | null;
  owner_name: string | null;
  next_follow_up_at: string | null;
  next_follow_up_note: string | null;
  /** 到点了没 —— **服务端判的**。前端拿本地时间比会跟提醒 worker 的口径对不上 */
  overdue: boolean;
}

export interface QuoteEntry {
  _id: string;
  quote_no: string;
  customer_id: string;
  items: Array<{
    factory_sku?: string | null;
    title?: string | null;
    quantity: number;
    unit_price: number;
  }>;
  currency: string;
  total: number;
  valid_until: string | null;
  status: 'draft' | 'sent' | 'accepted' | 'lost' | 'expired';
  order_id: string | null;
  /** 在线付款（10-23，Stripe）。老报价没有这个字段 = 没付过 */
  payment?: {
    status: 'unpaid' | 'pending' | 'paid' | 'refunded';
    amount_paid: number | null;
    currency: string | null;
    paid_at: string | null;
  } | null;
  created_at: string;
  // ── 报价单页（这几个字段接口一直在回，只是以前没人读）──────────────
  /** 版本号；改价 = 新文档新链接，旧链接冻结 */
  rev?: number;
  /** 直达 token —— 跟 view_base_url 拼出客户手上那个链接 */
  view_token?: string | null;
  view_base_url?: string | null;
  /** PI 抬头与条款（给客户看的单号在 pi.pi_no） */
  pi?: { pi_no?: string | null } | null;
}

/** 报价单总览的一行 */
export interface QuoteListEntry {
  _id: string;
  quote_no: string;
  /** 给客户看的 PI 号；老数据没有 */
  pi_no: string | null;
  rev: number;
  status: string;
  currency: string;
  total: number;
  goods_subtotal: number;
  items_count: number;
  valid_until: string | null;
  created_at: string;
  created_by_name: string | null;
  customer: { id: string; name: string } | null;
  view_url: string | null;
  site_slug: string | null;
  superseded: boolean;
  /** 在线付款（10-23，Stripe）。null = 没付过 */
  payment?: {
    status: 'unpaid' | 'pending' | 'paid' | 'refunded';
    amount_paid: number | null;
    currency: string | null;
    paid_at: string | null;
    test?: boolean;
  } | null;
  ref_inquiry_id: string | null;
}

/** AI 从对话里读出来的报价草稿 */
export interface QuoteAiFillResult {
  ok: true;
  items: Array<{
    factory_sku: string;
    title: string | null;
    image_url: string | null;
    spec: string | null;
    quantity: number;
    /** 模型说这一行是从哪句话来的，给业务员核对 */
    why: string | null;
  }>;
  buyer: { name: string; address: string; tel: string };
  port_of_destination: string;
  country_of_destination: string;
  shipment: string;
  note: string;
  /** 客户提了但没匹配上货号的 */
  unresolved: string[];
  /** 模型给了但不在货盘里、被服务端丢掉的货号 */
  dropped_skus: string[];
  scanned_messages: number;
}

/** 开报价单时摆出来的一条候选货 */
export interface QuoteCandidateEntry {
  factory_sku: string;
  global_sku: string | null;
  title: string | null;
  image_url: string | null;
  spec: string | null;
  last_quoted: { unit_price: number; currency: string; at: string; quote_no: string } | null;
  cost_cny: number | null;
  suggested_quantity: number | null;
  sources: Array<'quoted' | 'mentioned'>;
}

/** 系统自己定出来的「这个客户的报价单链接挂哪个站」；null = 定不出来，让业务员选 */
export interface QuoteSiteHint {
  slug: string;
  reason: 'last_quote' | 'site_inquiry' | 'inbound_endpoint';
  endpoint_label: string | null;
}

/** 这条报价的客户链接；CRM 页上直接建的（没有站点宿主）返回 null */
export function quoteViewUrl(q: QuoteEntry): string | null {
  if (!q.view_base_url || !q.view_token) return null;
  return `${q.view_base_url.replace(/\/$/, '')}/q/?qt=${encodeURIComponent(q.view_token)}`;
}

export interface ListQuery {
  [k: string]: unknown;
  limit?: number;
  before?: string;
  customer_id?: string;
  channel?: string;
  event_type?: string;
  decision_type?: string;
  module?: string;
  human_review?: string;
  status?: string;
  business_action?: string;
  site_slug?: string;
  direction?: 'inbound' | 'outbound' | 'system';
  /** 只看从这个号 / 邮箱进来的客户（值 = CustomerEvent.inbound_to） */
  inbound_to?: string;
  /** 只看从这个渠道进来的客户（目前只有 `site` = 独立站表单） */
  inbound_channel?: 'site';
  // P2 漏项：跟进筛选
  stage?: string;
  owner_user_id?: string;
  overdue?: string;
  /** 工厂端消息页分组：我认领的 / 没人认领的 / 同事认领的 / 全部（全部也会带回各组人数） */
  claim?: 'mine' | 'public' | 'others' | 'all';
  // iter3: outbound-sends
  draft_id?: string;
  email?: string;
  severity?: 'success' | 'soft_fail' | 'hard_fail' | 'complaint' | 'engagement' | 'admin';
  event?: string;
  // iter3: push-events
  decision_id?: string;
  fell_back?: 'true' | 'false';
  sla_violation?: 'true' | 'false';
  // iter3: leads
  organization?: string;
  country?: string;
  intent_tier?: 'cold' | 'warm' | 'hot' | 'won';
  assignee_id?: string;
  q?: string;
}

/** F4 出站 SendGrid 事件落库 — admin /crm/outreach/history 列表 */
export interface OutboundSendEntry {
  _id: string;
  sg_event_id: string;
  email: string;
  event: string;
  severity: 'success' | 'soft_fail' | 'hard_fail' | 'complaint' | 'engagement' | 'admin';
  timestamp: string;
  draft_id?: string | null;
  customer_id?: string | null;
  trace_id?: string | null;
  message_id?: string | null;
  reason?: string | null;
  type?: string | null;
  bus_topic?: string | null;
}

/** 入站渠道配置状态 — admin /crm/inbound/channels 页（GET /inbound/channels/status） */
export interface InboundChannelEnvRequirement {
  name: string;
  required: boolean;
  /** 是否已设置（不回显值） */
  present: boolean;
}
export interface InboundChannelStatus {
  channel: 'site-form' | 'email' | 'wa' | 'messenger' | 'fb' | 'ig' | 'youtube' | 'b-email-reply';
  label: string;
  in_contract_scope: boolean;
  implemented: boolean;
  configured: boolean;
  env: InboundChannelEnvRequirement[];
  missing_required: string[];
  detail: string;
}
export interface InboundChannelsReport {
  ok: true;
  channels: InboundChannelStatus[];
  youtube_poller_enabled: boolean;
  generated_at: string;
}

/* ── 工厂批量建联（B'3，10-27） ── */
export interface FactoryTemplate {
  id: string;
  scenario: string;
  scenario_label: string;
  category: string;
  version: string;
  sample: { subject: string; body: string };
  /** 话术针对的材质标签；通用话术为空 */
  material_keys: string[];
}
export interface FactoryTag {
  key: string;
  label: string;
}
export interface FactoryTargetRow {
  supplier_id: string;
  name: string;
  category: string[];
  materials: string[];
  products: string[];
  contact_name: string | null;
  email: string | null;
  cooperation_status: string | null;
  eligible: boolean;
  skip_reason: string | null;
  last_contacted_at: string | null;
}
export interface FactoryPhoneTask {
  supplier_id: string;
  name: string | null;
  contact: string | null;
  phone: string;
  reason: string;
}
export type FactoryReplyClass =
  | 'has_capacity'
  | 'no_capacity'
  | 'need_more'
  | 'not_fit'
  | 'no_reply';

export interface FactoryReply {
  _id: string;
  via: 'sourcing_mailbox' | 'sales_fallback' | 'sweep';
  supplier_id: string | null;
  supplier_name: string | null;
  draft_id: string | null;
  campaign_name: string | null;
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  text: string | null;
  received_at: string | null;
  class: FactoryReplyClass | null;
  class_by: 'ai' | 'human' | 'system' | null;
  ai: {
    class: string | null;
    confidence: number | null;
    summary: string | null;
    facts: {
      min_order_qty: number | null;
      lead_time_days: number | null;
      capacity_monthly_pcs: number | null;
      price_note: string | null;
      contact_name: string | null;
      phone: string | null;
      wechat: string | null;
    } | null;
    opt_out: boolean;
    error: string | null;
  } | null;
  follow: {
    status: 'open' | 'done' | 'none';
    assignee_name: string | null;
    notified_at: string | null;
    note: string | null;
    handled_by: string | null;
    handled_at: string | null;
  } | null;
  supplier_changes: string[];
  created_at: string;
}

export interface FactoryCampaign {
  _id: string;
  name: string;
  template_id: string;
  status: 'running' | 'paused' | 'done' | 'canceled' | 'failed';
  daily_cap: number;
  counts: { queued: number; sent: number; skipped: number; failed: number };
  day_sent: number;
  created_by: string | null;
  created_at: string;
  finished_at: string | null;
  error: string | null;
  log?: Array<{
    at: string;
    supplier_name: string | null;
    email: string | null;
    result: string;
    reason: string | null;
  }>;
  phone_tasks?: FactoryPhoneTask[];
  phone_tasks_notified_at?: string | null;
}

/* ── 触达漏斗（B3，10-27） ── */
export type OutreachStage = 'sent' | 'delivered' | 'opened' | 'replied' | 'interested' | 'won';
export type OutreachOutcome = 'won' | 'lost' | 'no_response' | 'following';
export type OutreachDimension = 'category' | 'country' | 'template' | 'campaign' | 'language';
export interface OutreachCounts extends Record<OutreachStage, number> {
  requested: number;
  failed: number;
  bounced: number;
  clicked: number;
  unsubscribed: number;
  complained: number;
}
export interface OutreachFunnelQuery {
  days?: number;
  audience?: 'buyer' | 'factory';
  campaign?: string;
  category?: string;
  country?: string;
  template_id?: string;
}
export interface OutreachFunnelResponse {
  ok: boolean;
  stages: OutreachStage[];
  total: OutreachCounts;
  by: Record<
    OutreachDimension,
    Array<{ key: string; counts: OutreachCounts; subjects?: string[] }>
  >;
  subjects: Array<{ key: string; template: string; counts: OutreachCounts }>;
  outcomes: {
    total: number;
    by_outcome: Partial<Record<OutreachOutcome, number>>;
    lost_reasons: Array<{ reason: string; n: number }>;
  };
  channel: {
    send_enabled: boolean;
    bulk_ready: boolean;
    events_enabled: boolean;
    detail: string;
    feishu_quota: Array<{
      from: string;
      day: string;
      used: number;
      limit: number;
      exhausted: boolean;
    }>;
  };
  generated_at: string;
}
export interface OutreachSendRow {
  draft_id: string;
  customer_id: string | null;
  audience: string;
  to_email: string | null;
  campaign: string | null;
  template_id: string | null;
  template_version: string | null;
  subject: string | null;
  language: string | null;
  category: string | null;
  country: string | null;
  status: string;
  fail_reason: string | null;
  requested_at: string;
  sent_at: string | null;
  bounced: boolean;
  unsubscribed: boolean;
  replied_at: string | null;
  intent: string | null;
  order_id: string | null;
  outcome: OutreachOutcome | null;
  outcome_reason: string | null;
  outcome_by: string | null;
  stages: Record<OutreachStage, boolean>;
}

/** 出站渠道配置状态 — admin /crm/outbound/channels 页（GET /outbound/channels/status） */
export interface OutboundChannelEnvRequirement {
  name: string;
  required: boolean;
  /** 是否已设置（不回显值） */
  present: boolean;
}
export interface OutboundChannelStatus {
  channel: 'email' | 'whatsapp';
  label: string;
  in_contract_scope: boolean;
  implemented: boolean;
  configured: boolean;
  env: OutboundChannelEnvRequirement[];
  missing_required: string[];
  detail: string;
}
export interface OutboundChannelsReport {
  ok: true;
  send_enabled: boolean;
  dispatcher_enabled: boolean;
  events_enabled: boolean;
  sandbox: boolean;
  channels: OutboundChannelStatus[];
  email_ready: boolean;
  generated_at: string;
}

/** F5 飞书派发记录 — admin /crm/push-log 列表 */
export interface PushEventEntry {
  _id: string;
  decision_id: string;
  customer_id: string;
  module: string;
  dedupe_key: string;
  dispatched_at: string;
  status: 'dispatched' | 'acked' | 'escalated_l1' | 'escalated_l2' | 'failed_terminal';
  acked_at?: string | null;
  ack_user_id?: string | null;
  ack_action?: string | null;
  escalation_level?: number;
  escalation_at?: string | null;
  fell_back?: boolean;
  error_kind?: string | null;
  error_message?: string | null;
  recipient?: {
    receive_id_type?: string;
    receive_id?: string;
    salesperson_id?: string | null;
  };
  trace_id?: string | null;
}

/** F5 push rule — admin /crm/push-rules 列表 + CRUD */
export interface InboundEndpointEntry {
  _id: string;
  channel: string;
  address: string;
  address_normalized: string;
  label: string;
  site_slug: string | null;
  note: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PushRuleEntry {
  _id?: string;
  rule_id: string;
  name: string;
  enabled: boolean;
  priority: number;
  scope: {
    channels: string[];
    regions: string[];
    priority_levels: Array<'A' | 'B' | 'C'>;
    salesperson_ids: string[];
  };
  schedule: {
    tz_strategy: 'salesperson' | 'customer' | 'utc8' | 'fixed';
    fixed_tz_offset_hours: number;
    quiet_start_hour: number;
    quiet_end_hour: number;
    urgent_override: boolean;
    quiet_action: 'defer' | 'skip';
  };
  created_at?: string;
  updated_at?: string;
}

/** Customer.contact_blocks 子文档 — admin /crm/blocks 列表 */
export interface ContactBlockEntry {
  email: string;
  block_type: string;
  blocked_at: string | null;
  reason: string | null;
}

/** ContactBlockAudit 行 — admin /crm/blocks/audit 列表 */
export interface ContactBlockAuditEntry {
  customer_id: string;
  email: string;
  action: 'add' | 'remove';
  block_type: 'bounced' | 'complained' | 'unsubscribed' | 'admin_manual' | null;
  result: string;
  actor: string;
  reason: string | null;
  request_id: string | null;
  created_at: string;
}

/** C8 网关 LlmCall 聚合桶 — admin /crm/gateway */
export interface LlmStatsBucket {
  count: number;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  cost_usd: number;
  avg_latency_ms: number;
  cached_count: number;
  error_count: number;
}
export interface LlmStatsResponse {
  ok: true;
  total: number;
  group_by: string;
  groups: Record<string, LlmStatsBucket>;
}

/** Customer = lead 视图 — admin /crm/leads 列表 */
/** 客户在哪儿、那边几点 —— **服务端算好的**，前端不做映射（见 crm-schema/customer-locale.ts） */
export interface CustomerLocale {
  country: string | null;
  country_label: string | null;
  timezone: string | null;
  /** 多时区国家按主用时区估的 → 界面写「约」 */
  approximate: boolean;
  source: 'phone' | 'country' | null;
}

/**
 * 一个「入站账号」= 客户能找到我们的一个入口：公司的 WhatsApp 商户号、
 * 某个业务员自己扫码绑的号、收件邮箱、官网表单。
 * 业务员切「领空」切的就是它。
 */
export interface InboundAccount {
  channel: string | null;
  address: string;
  label: string | null;
  messages: number;
  customers: number;
  last_at: string | null;
  /** `relay` = 扫码绑的号（某个人自己的）；`cloud` = 公司号 / 邮箱 / 官网表单 */
  kind?: 'relay' | 'cloud';
  owner_salesperson_id?: string | null;
  owner_name?: string | null;
  /** 这个号是不是我自己绑的 */
  mine?: boolean;
  relay_status?: string | null;
}

export interface LeadEntry {
  _id: string;
  display_name?: string | null;
  /** WhatsApp 头像的私有存储键；有值才去 /wa-relay/avatar/:id 取图 */
  avatar_key?: string | null;
  organization?: string | null;
  organization_domain?: string | null;
  country?: string | null;
  emails?: string[];
  phones?: string[];
  intent_tier?: 'cold' | 'warm' | 'hot' | 'won' | null;
  intent_score?: number | null;
  status?: 'active' | 'archived' | 'do_not_contact' | 'merged';
  assignee_id?: string | null;
  /** 负责人名字（服务端 join，前端不做 id→名字映射） */
  assignee_name?: string | null;
  /** 这个客户是不是我在跟 */
  mine?: boolean;
  channels_seen?: string[];
  last_seen_at?: string | null;
  first_seen_at?: string | null;
  /** 我方最后一次回复的时间；null = 从来没回过 */
  last_replied_at?: string | null;
  last_replied_channel?: string | null;
  /** 最新一条客户消息（列表上业务员要看的是"这人最后说了什么"） */
  last_inbound_text?: string | null;
  last_inbound_text_zh?: string | null;
  last_inbound_at?: string | null;
  last_inbound_channel?: string | null;
  /** 这条是打到我们哪个号 / 哪个邮箱的（号一多，「这条该谁跟」第一判据就是它） */
  last_inbound_to?: string | null;
  last_inbound_to_label?: string | null;
  /** 这条走的腿；`wa_relay` = 扫码托管的号（没有 24 小时窗口） */
  last_inbound_transport?: string | null;
  /** 我方最近一条发出（10-12）：左栏预览取它和客户那条里较晚的一条 */
  last_outbound_text?: string | null;
  last_outbound_at?: string | null;
  last_outbound_channel?: string | null;
  /** 客户在我上次打开之后发了几条（只有工厂账号有） */
  unread_count?: number;
  /* ── P2 漏项：跟进（stage_label 由服务端拼，前端不做 key→文案映射）── */
  stage?: string;
  stage_label?: string;
  owner_user_id?: string | null;
  next_follow_up_at?: string | null;
  next_follow_up_note?: string | null;
  locale?: CustomerLocale | null;
  /** country 字段本身的中文名（服务端算，`CN` → 中国，认不出原样） */
  country_label?: string | null;
}

function buildQuery(q: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface BpSourcingTaskEntry {
  _id: string;
  q: string;
  filter?: string | null;
  trigger: 'chat' | 'inquiry' | 'admin';
  initiator_name?: string | null;
  customer_id?: string | null;
  status: 'running' | 'done' | 'failed';
  candidate_count: number;
  api_calls: number;
  error?: string | null;
  created_at: string;
}

export interface BpSourcingCandidateEntry {
  _id: string;
  num_iid: string;
  title?: string | null;
  company_name?: string | null;
  main_products?: string[];
  price?: string | null;
  min_num?: number | null;
  sales?: number | null;
  year_started?: string | null;
  customer_star?: string | null;
  score: number;
  rank: number;
  detail_url?: string | null;
  shop_url?: string | null;
  ingested?: boolean;
}

/**
 * 阿里寻源 SSE 帧 —— 与后端 bp-sourcing-service.ts BpSourcingEvent + bp-sourcing.ts 路由帧对齐。
 * ready/heartbeat/final/fatal 是传输控制,其余是 runSourcing 各阶段进度。
 */
export type BpSourcingStreamEvent =
  | { type: 'ready'; ts: number }
  | { type: 'heartbeat'; ts: number }
  | { type: 'search_start'; ts: number; q: string }
  | { type: 'search_done'; ts: number; total: number; taken: number }
  | { type: 'detail_progress'; ts: number; done: number; total: number }
  | { type: 'detail_skip'; ts: number; num_iid: string; reason: string }
  | { type: 'company_start'; ts: number; total: number }
  | { type: 'company_progress'; ts: number; done: number; total: number }
  | { type: 'company_skip'; ts: number; sid: string; reason: string }
  | { type: 'scoring'; ts: number; count: number }
  | { type: 'done'; ts: number; task_id: string; candidate_count: number; api_calls: number }
  | { type: 'final'; ts: number; task_id: string; candidate_count: number; api_calls: number }
  | { type: 'fatal'; ts: number; message: string };

export type PolishStage = 'context' | 'polish' | 'check';
export type PolishStreamEvent =
  | { type: 'stage'; stage: PolishStage; status: 'start' | 'done'; detail?: string; ts: number }
  | {
      type: 'done';
      ok: true;
      reply: string;
      target_language: string;
      absence_rewritten: boolean;
      warnings: string[];
      latency_ms: number;
    }
  | { type: 'error'; reason: string; message: string }
  | { type: 'heartbeat'; ts: number };

// ── D1 订单流程与订单 ─────────────────────────────────────────────────
// 七个语种的 key 跟后端 ORDER_FLOW_LANGS 一致（zh + CUSTOMER_LANGS）。
// 前端不另定一套，否则「多语言覆盖 100%」会有两个打架的口径。
export const ORDER_FLOW_LANGS = ['zh', 'en', 'es', 'fr', 'ar', 'ru', 'pt'] as const;
export type OrderFlowLang = (typeof ORDER_FLOW_LANGS)[number];

export const ORDER_FLOW_LANG_LABEL: Record<OrderFlowLang, string> = {
  zh: '中文',
  en: '英语',
  es: '西班牙语',
  fr: '法语',
  ar: '阿拉伯语',
  ru: '俄语',
  pt: '葡萄牙语',
};

export interface OrderFlowNodeEntry {
  key: string;
  order: number;
  labels: Record<string, string>;
  is_terminal: boolean;
  is_public: boolean;
  /** D6：到这一步要工厂动手 → 推租户通知 */
  needs_tenant_action?: boolean;
  /** D6：到这一步要提醒内部业务员（只给异常节点开） */
  alert_internal?: boolean;
  color: string | null;
}

export interface OrderFlowEntry {
  _id: string;
  key: string;
  name: string;
  is_default: boolean;
  nodes: OrderFlowNodeEntry[];
  node_count?: number;
  order_count?: number;
  created_at?: string;
}

export interface OrderEntry {
  _id: string;
  po_number: string;
  customer_id: string;
  customer_name: string | null;
  /** 下单入口：manual（业务员建）/ cart（买家自助）/ quote（报价转）/ import（批量导入）/ stripe（在线付款） */
  source?: string;
  /** 10-23：从哪份报价来、什么时候收到钱 */
  quote_id?: string | null;
  paid_at?: string | null;
  /** 采购这一侧：not_needed（工厂自己的单）/ pending_approval / approved / rejected（采购退回） */
  purchasing?: {
    status: 'not_needed' | 'pending_approval' | 'approved' | 'rejected';
    approval_instance: string | null;
    note: string | null;
  } | null;
  flow_key: string;
  status: string;
  status_label: string;
  total_usd: number | null;
  total_cny: number | null;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface OrderEventEntry {
  _id: string;
  order_id: string;
  type: string;
  from_status: string | null;
  to_status: string | null;
  from_label?: string | null;
  to_label?: string | null;
  by_type: string;
  by_name: string | null;
  note: string | null;
  ts: string;
}

export interface OrderItemEntry {
  factory_sku?: string | null;
  title?: string | null;
  quantity: number;
  unit_price_usd?: number | null;
  unit_price_cny?: number | null;
  notes?: string | null;
}

export interface OrderShippingAddress {
  name?: string | null;
  country?: string | null;
  detail?: string | null;
  phone?: string | null;
}

export interface OrderDetail {
  ok: true;
  order: OrderEntry & {
    items?: OrderItemEntry[];
    status_label: string;
    payment_terms?: string | null;
    deposit_pct?: number | null;
    shipping_method?: string | null;
    shipping_address?: OrderShippingAddress | null;
    carrier?: string | null;
    tracking_no?: string | null;
    notes?: string | null;
  };
  flow: {
    key: string;
    name: string;
    nodes: Array<{
      key: string;
      label: string;
      is_terminal: boolean;
      is_public: boolean;
      color: string | null;
    }>;
  } | null;
  events: OrderEventEntry[];
}

/** D2 改单可改的字段。**没有 po_number / flow_key / tenant_id** —— 后端也不收 */
export interface OrderPatchBody {
  /** 乐观锁版本号 = 前端拿到的那一版的 updated_at */
  updated_at: string;
  customer_id?: string;
  supplier_id?: string;
  items?: OrderItemEntry[];
  total_usd?: number | null;
  total_cny?: number | null;
  currency?: string;
  payment_terms?: string | null;
  deposit_pct?: number | null;
  shipping_method?: string | null;
  shipping_address?: OrderShippingAddress | null;
  carrier?: string | null;
  tracking_no?: string | null;
  notes?: string | null;
}

export interface OrderFieldChange {
  field: string;
  label: string;
  from: unknown;
  to: unknown;
}

// ── D2 批量导入 ──────────────────────────────────────────────────────
export interface OrderImportRow {
  line: number;
  po_number: string;
  ok: boolean;
  errors: string[];
  resolved?: {
    customer_name: string;
    supplier_name: string;
    flow_key: string;
    status: string;
    items: number;
    total_usd: number | null;
  };
}

export interface OrderImportDryRun {
  ok: boolean;
  total: number;
  valid: number;
  invalid: number;
  rows: OrderImportRow[];
  fatal: string[];
  max_rows?: number;
}

// ── P3 WhatsApp 扫码托管 ─────────────────────────────────────────────
/** 会话状态的安全投影。**没有凭据，没有二维码** —— 那两样各有各的去处 */
/** 工厂的一个 WhatsApp 号（号位） */
export interface WaAccountView extends WaRelaySessionView {
  account_id: string;
  /**
   * `cloud` = 公司的 WhatsApp Cloud API 官方号（只有自营那家工厂 Strand 有）：
   * 不用扫码、一直在线、不占号位、不能解绑。`relay` / 缺省 = 扫码绑的号。
   */
  kind?: 'cloud' | 'relay';
  slot: number;
  label: string | null;
  /** 现在真正绑着（连着或断线重连中） */
  bound: boolean;
  has_egress: boolean;
  /** 这个号上有未读的会话数 */
  unread: number;
  /** 这个号上客户说完最后一句我们还没回的会话数 */
  waiting: number;
  last_message_at: string | null;
  /** 号主（扫码绑这个号的子账号）；null = 老号，主账号管 */
  owner_tenant_user_id?: string | null;
  /** 号主 id（工厂 = 子账号，自营 = 内部业务员）；null = 主账号 / 管理员管 */
  owner_id?: string | null;
  owner_name?: string | null;
  /** 没人认领的客户谁看得到 */
  visibility?: 'owner_only' | 'all' | 'members';
  visible_user_ids?: string[];
  /** 当前登录的人能不能改可见范围 / 解绑 / 重扫（号主或主账号） */
  can_manage?: boolean;
  is_owner?: boolean;
}

/** 本厂一个在用账号 */
export interface PortalMember {
  id: string;
  name: string;
  role: 'owner' | 'supervisor' | 'member';
}

export interface WaRelaySessionView {
  /** 这一路（号位）的 id（09-23 一家最多 5 个号） */
  account_id?: string | null;
  /** 号位序号 1..5：决定这个号在工厂端的固定颜色 */
  slot?: number;
  /** 工厂给这个号起的备注名；null = 显示号码 */
  label?: string | null;
  tenant_id: string | null;
  status:
    | 'init'
    | 'qr_pending'
    | 'connected'
    | 'reconnecting'
    | 'logged_out'
    | 'banned'
    | 'disconnected';
  phone_number: string | null;
  display_name: string | null;
  connected_at: string | null;
  disconnected_at: string | null;
  last_activity_at: string | null;
  reconnect_attempts: number;
  messages_in: number;
  messages_out: number;
  last_error: string | null;
  /** 这一路从哪个公网 IP 出去（出口池的 tag）；null = 走机器默认 IP */
  egress_tag?: string | null;
  /** 绑定时记下的那个真实公网 IP —— 换了就说明出口被动过，会拒绝连接 */
  egress_public_ip?: string | null;
  /** 这个号归哪个业务员（自营那条腿才有） */
  owner_salesperson_id?: string | null;
}

export interface WaRelayAdminEntry extends WaRelaySessionView {
  tenant_name: string | null;
  tenant_slug: string | null;
  enabled: boolean;
  /** null = 问不到 wa-bridge。「不知道」和「确定断了」是两回事 */
  live: boolean | null;
}

// ── D3 物流轨迹 ──────────────────────────────────────────────────────
export interface TrackingEntry {
  _id: string;
  ts: string;
  status: string;
  status_label: string;
  location: string | null;
  description: string | null;
  source: string;
  provider: string | null;
  created_by: string | null;
}

export interface OrderTracking {
  ok: true;
  shipment: {
    carrier: string | null;
    tracking_no: string | null;
    shipping_method: string | null;
    last_tracking_at: string | null;
    delivered_at: string | null;
  };
  entries: TrackingEntry[];
  statuses: Array<{ key: string; label: string }>;
}

// ── P2 客户资产池 ─────────────────────────────────────────────────────
export interface TimelineEntry {
  id: string;
  ts: string;
  type: string;
  direction: string | null;
  channel: string | null;
  /** 服务端生成的中文短句 —— 前端不做 type→文案映射（租户侧以后要多语种，放服务端只改一处） */
  title: string;
  excerpt: string | null;
  excerpt_zh: string | null;
  ref: { inquiry_id?: string; order_id?: string };
  actor: { type: string; name: string | null };
}

export interface MergeCandidateEntry {
  customer_id: string;
  display_name: string | null;
  organization: string | null;
  country: string | null;
  emails: string[];
  phones: string[];
  last_seen_at: string | null;
  reasons: string[];
  confidence: number;
  strong: boolean;
}

// ── 租户通知基础设施 ──────────────────────────────────────────────────

export interface TenantNotifySettings {
  channel: 'feishu' | 'wechat' | 'both' | 'none';
  feishu_webhook_url: string | null;
  wechat_openids: string[];
  /** 没配过的键 = 开着（配了通道就是想收通知） */
  events?: Record<string, boolean>;
  quiet_hours?: { enabled: boolean; tz?: string; start?: string; end?: string };
}

export interface TenantNotificationEntry {
  _id: string;
  tenant_id: string;
  tenant_name: string | null;
  event_type: string;
  channel: 'feishu' | 'wechat';
  tried_channels?: string[];
  status: 'pending' | 'deferred' | 'sent' | 'failed';
  attempts?: number;
  last_error?: string | null;
  sent_at?: string | null;
  created_at?: string;
}

// ── 免费联营派单 ──────────────────────────────────────────────────────

/** 候选工厂。`tenant_id` 为空 = 台账里有这家厂但还没开通账号，派不了 */
export interface FactoryCandidate {
  supplier_id: string | null;
  supplier_name: string;
  tenant_id: string | null;
  tenant_name: string | null;
  /** 有账号且至少一个人绑了微信 —— 只有这种派过去才收得到通知 */
  bound: boolean;
  from: 'match' | 'search';
  already_assigned: boolean;
}

/** 话题里的一款货 */
export interface TopicSku {
  key: string;
  product_id: string | null;
  sku_code: string | null;
  title: string | null;
  spec: string | null;
  quantity: number | null;
  image_url: string | null;
  sources?: string[];
}

/** 一个 SKU 话题（客户 + 货 + 工厂 + 授权的消息） */
export interface AssignmentEntry {
  _id: string;
  tenant_id: string;
  tenant_name: string | null;
  status: 'active' | 'revoked' | 'completed';
  assigned_at: string;
  assigned_by: string | null;
  note: string | null;
  first_response_at: string | null;
  feedbacks?: Array<{ at: string; by: string | null; reason: string | null }>;
  skus: TopicSku[];
  /** 「ST-1023 925 银手链」/「ST-1023 925 银手链 等 2 款」 */
  sku_label: string;
  event_ids: string[];
  /** 话题里最新一条是工厂回的 = replied；是买家的 = waiting（since 起算等了多久） */
  reply: { state: 'replied' | 'waiting' | 'none'; since: string | null };
  revoked_at?: string | null;
}

/** 转单第一步的一款候选货 */
export interface SkuOption extends TopicSku {
  sources: Array<'inquiry' | 'quote' | 'chat' | 'match'>;
  held_by: { assignment_id: string; tenant_id: string; tenant_name: string | null } | null;
}

/** 搜货搜出来的一款货 */
export interface GoodsHit {
  key: string;
  product_id: string;
  sku_code: string | null;
  title: string | null;
  title_en: string | null;
  spec: string | null;
  image_url: string | null;
  price_cny: number | null;
  moq: number | null;
  url_1688: string | null;
  held_by: { tenant_id: string; tenant_name: string | null } | null;
}

/** 搜货结果按工厂分的一组 */
export interface GoodsFactory {
  supplier_id: string;
  supplier_name: string;
  region: string | null;
  contact: { name: string | null; phone: string | null; wechat: string | null } | null;
  url_1688: string | null;
  tenant_id: string | null;
  tenant_name: string | null;
  bound: boolean;
  already_assigned: boolean;
  matched: number;
  products: GoodsHit[];
}

/** 转单第二步的一条消息 */
export interface ContextOption {
  _id: string;
  direction: 'inbound' | 'outbound';
  text: string;
  ts: string | null;
  channel: string | null;
  has_attachment: boolean;
  suggested: boolean;
  in_topics: string[];
}

/** 一次推送决定（系统判的 / 人推的） */
export interface RelayRoutingEntry {
  _id: string;
  event_id: string;
  assignment_id: string | null;
  auto: boolean;
  method: 'quote' | 'sku' | 'llm' | 'manual' | 'none';
  status: 'active' | 'withdrawn' | 'moved' | 'unrouted' | 'dismissed';
  confidence: number | null;
  reason: string | null;
  candidate_ids: string[];
  created_at: string;
}

export interface RelayAccuracy {
  days: number;
  auto_pushed: number;
  undone: number;
  unrouted: number;
  accuracy: number | null;
}

// ── G2 工厂身份绑定 ───────────────────────────────────────────────────
export interface BindRequestEntry {
  _id: string;
  wechat_openid: string;
  wechat_avatar_url: string | null;
  claimed_company: string | null;
  claimed_phone: string | null;
  claimed_contact: string | null;
  status: string;
  created_at: string;
  supplier_candidates: Array<{
    _id: string;
    name: string;
    short_name: string | null;
    cooperation_status: string | null;
  }>;
}

export interface BindDashboard {
  ok: true;
  stats: { supplier_total: number; bound: number; pending: number; week_new: number };
  bound: Array<{
    _id: string;
    tenant_id: string;
    tenant_name: string | null;
    name: string;
    phone: string | null;
    bound_at: string;
    last_login_at: string | null;
  }>;
}

// ── G4 工厂端 ────────────────────────────────────────────────────────
export interface PortalSummary {
  ok: true;
  todo: {
    pending_reply: number;
    new_inquiries: number;
    open_orders: number;
    /** TikTok 评论等你回复的条数；没绑过 TikTok 是 null（首页不画这一行） */
    social_pending?: number | null;
  };
}

export interface PortalReplyWindow {
  closes_at: string | null;
  closed: boolean;
  remaining_ms: number | null;
  /** 不受 24 小时窗口约束时的原因（托管号 = 'wa_relay_no_window'）；受限时不带 */
  reason?: string | null;
}

/** 撮合推荐：**只有这四个字段**。成本、货源、供应商名一律不出现在工厂端 */
export interface SuggestedProduct {
  sku_code: string | null;
  title: string;
  image: string | null;
  match_level: 'high' | 'mid' | 'low';
}

export interface InquirySkuCard {
  sku_code: string;
  title: string | null;
  image: string | null;
  /** 站上产品页；站上没有 / 已下架 → null */
  site_url: string | null;
  hidden_on_site: boolean;
  /** 后台产品详情页路径，只给内部账号 */
  admin_path: string | null;
  /** 购物车里客户填的数量；表单「感兴趣的 SKU」没有数量 → null（旧后端不给 → undefined） */
  quantity?: number | null;
  /** 客户提交时站上的单价，原样（如 `$8.91`） */
  price_label?: string | null;
}

export interface PortalConversation {
  ok: true;
  customer: Record<string, unknown>;
  messages: Array<Record<string, unknown>>;
  reply_window: PortalReplyWindow;
  channel: string | null;
  pending_draft: Record<string, unknown> | null;
  /** 这家工厂手上对得上的货；空数组时整块不渲染 */
  suggested_products?: SuggestedProduct[];
  /** 网站表单询盘里点到的货（按事件 id）：小图 + 站上产品页 + 后台产品页 */
  sku_cards?: Record<string, InquirySkuCard[]>;
  assigned?: boolean;
  customer_code?: string | null;
  /**
   * 认领（10-20）：谁认领了这个客户、我能不能认领 / 改回公共。
   * 平台转单的客户为 null（归平台，不在工厂内部认领）。
   */
  claim?: {
    owner_user_id: string | null;
    owner_name: string | null;
    mine: boolean;
    can_claim: boolean;
    can_release: boolean;
  } | null;
  /** 平台转单的话题：SKU 卡片 + 代发时自动加的开头标记 */
  topic?: {
    assignment_id: string;
    skus: Array<{
      sku_code: string | null;
      title: string | null;
      spec: string | null;
      quantity: number | null;
      image: string | null;
    }>;
    reply_prefix: string;
  } | null;
  /**
   * 邮件客户（站点表单 / 邮件进来的）回复怎么发：发到谁、用哪个邮箱发、主题。
   * WhatsApp 客户 / 平台转来的单 → null。from 为 null = 这个站没配发件邮箱，走全局兜底。
   */
  email_route?: { to: string | null; from: string | null; subject: string } | null;
  /** 官方号 WhatsApp 过了 24 小时、能改发邮件时才有：发到谁、用哪个邮箱发 */
  email_fallback?: { to: string | null; from: string | null; subject: string } | null;
  /** 我方哪几条其实没发出去（message_out 的 ref_message_id） */
  failed_message_ids?: string[];
}

/* ── H2 工厂端 AI 能力 ───────────────────────────────────────────── */

/** 找供应链的一张脱敏卡。**没有厂名、没有联系方式、没有数字价** —— 后端就不给 */
export interface SourcingCandidateCard {
  ref: string;
  categories: string[];
  capacity: 'small' | 'medium' | 'large' | 'unknown';
  lead_time_days: number | null;
  price_band: 'within' | 'slightly_above' | 'above' | 'unknown';
  matched_items: number;
  quote_requested?: boolean;
}

export interface PortalSourcingRequest {
  _id: string;
  need: string;
  quantity: number | null;
  target_price_cny: number | null;
  lead_time: string | null;
  status: 'open' | 'matched' | 'closed';
  created_at: string | null;
  customer_id: string | null;
  candidates: SourcingCandidateCard[];
}

export interface PortalAutoReply {
  enabled: boolean;
  delay_minutes?: number;
  business_hours?: { tz?: string; start?: string; end?: string };
  first_reply_only?: boolean;
}

/** 内部侧看到的同一条需求：多了真厂名（工厂那边永远拿不到） */
export interface InternalSourcingRequest extends Omit<PortalSourcingRequest, 'candidates'> {
  source_tenant_id: string;
  source_tenant_name: string;
  candidates: Array<
    SourcingCandidateCard & {
      supplier_id: string | null;
      supplier_name: string | null;
      dispatched_at: string | null;
    }
  >;
}

/**
 * 读一条 SSE（POST + `text/event-stream`）。
 *
 * **内部润色和工厂端润色共用这一份。** 两边各写一遍的话，
 * 半包处理、心跳行忽略、错误状态码这三处迟早分叉，而分叉的表现是
 * 「其中一边偶尔丢最后一帧」—— 那种问题没人查得出来。
 */
async function readSseStream<T>(
  url: string,
  body: unknown,
  onEvent: (evt: T) => void,
  abortSignal?: AbortSignal,
): Promise<void> {
  const init: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Crm-Module': 'admin',
      ...actAsHeaders(),
    },
    body: JSON.stringify(body),
  };
  if (abortSignal) init.signal = abortSignal;
  const res = await fetch(url, init);
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    // 后端在开流之前拒的（403 能力没开 / 404 不是你的客户）是 JSON，尽量还原出来
    let parsed: unknown = text;
    try {
      parsed = JSON.parse(text);
    } catch {
      /* 不是 JSON 就原样带上 */
    }
    throw new CrmApiError(res.status, parsed, `sse ${res.status}: ${text.slice(0, 200)}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    while (true) {
      const idx = buf.indexOf('\n\n');
      if (idx < 0) break;
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      for (const line of frame.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        try {
          onEvent(JSON.parse(line.slice(6)) as T);
        } catch {
          /* 心跳注释行 / 半包忽略 */
        }
      }
    }
  }
}

export const crmApi = {
  // ── D1 订单流程 ──
  listOrderFlows: () =>
    crmFetch<{ ok: true; entries: OrderFlowEntry[] }>('/api/v1/crm/order-flows'),
  getOrderFlow: (key: string) =>
    crmFetch<{ ok: true; flow: OrderFlowEntry; langs: string[] }>(
      `/api/v1/crm/order-flows/${encodeURIComponent(key)}`,
    ),
  createOrderFlow: (body: { key: string; name: string; nodes: OrderFlowNodeEntry[] }) =>
    crmFetch<{ ok: true; flow: OrderFlowEntry }>('/api/v1/crm/order-flows', {
      method: 'POST',
      body,
    }),
  patchOrderFlow: (key: string, body: { name?: string; nodes?: OrderFlowNodeEntry[] }) =>
    crmFetch<{ ok: true; flow: OrderFlowEntry }>(
      `/api/v1/crm/order-flows/${encodeURIComponent(key)}`,
      { method: 'PATCH', body },
    ),
  deleteOrderFlow: (key: string) =>
    crmFetch<{ ok: true; deleted: string }>(`/api/v1/crm/order-flows/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),

  // ── G5 档位能力矩阵（仅内部）──
  listTierCapabilities: () =>
    crmFetch<{
      ok: true;
      capabilities: string[];
      tiers: Array<{
        tier: string;
        label: string | null;
        note: string | null;
        configured: boolean;
        effective: Record<string, boolean>;
      }>;
    }>('/api/v1/crm/tenant-capabilities'),
  putTierCapabilities: (
    tier: string,
    body: { capabilities: Record<string, boolean>; label?: string | null; note?: string | null },
  ) =>
    crmFetch<{ ok: true; tier: string; effective: Record<string, boolean> }>(
      `/api/v1/crm/tenant-capabilities/${encodeURIComponent(tier)}`,
      { method: 'PUT', body },
    ),

  // ── P5 租户归属人工指派（仅内部）──
  listPendingAssignment: () =>
    crmFetch<{ ok: true; items: PendingAssignment[]; count: number }>(
      '/api/v1/crm/tenant-assignment/pending',
    ),
  assignTenant: (
    customerId: string,
    body: { tenant_id: string | null; by?: string; note?: string },
  ) =>
    crmFetch<{ ok: true; changed: boolean; from: string | null; to: string | null }>(
      `/api/v1/crm/tenant-assignment/${encodeURIComponent(customerId)}`,
      { method: 'POST', body },
    ),

  // ── D1 订单（只读 + 改状态；建单批量导入是 D2）──
  listOrders: (
    q: {
      status?: string;
      flow_key?: string;
      q?: string;
      limit?: number;
      /** 客户档案上的「订单」区就靠它。后端早就支持，之前只是前端没用 */
      customer_id?: string;
    } = {},
  ) =>
    crmFetch<{ ok: true; entries: OrderEntry[]; total: number }>(
      `/api/v1/crm/orders${buildQuery(q)}`,
    ),
  getOrder: (id: string, lang?: string) =>
    crmFetch<OrderDetail>(
      `/api/v1/crm/orders/${encodeURIComponent(id)}${lang ? `?lang=${lang}` : ''}`,
    ),
  createOrder: (body: {
    po_number: string;
    customer_id: string;
    supplier_id: string;
    flow_key?: string;
    status?: string;
    items: Array<{
      factory_sku?: string | null;
      title?: string | null;
      quantity: number;
      unit_price_usd?: number | null;
    }>;
    total_usd?: number | null;
  }) =>
    crmFetch<{ ok: true; order: { _id: string; po_number: string } }>('/api/v1/crm/orders', {
      method: 'POST',
      body,
    }),
  changeOrderStatus: (id: string, body: { status: string; note?: string }) =>
    crmFetch<{ ok: true; status: string; label: string }>(
      `/api/v1/crm/orders/${encodeURIComponent(id)}/status`,
      { method: 'POST', body },
    ),
  /** D2 改单。409 = 这单刚被人改过（body 里带最新值），要提示刷新而不是自动重试 */
  patchOrder: (id: string, body: OrderPatchBody) =>
    crmFetch<{ ok: true; changes: OrderFieldChange[]; updated_at: string }>(
      `/api/v1/crm/orders/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    ),

  // ── D2 批量导入（下模板 → 试跑 → 提交）──
  /** 模板走原生跳转（要走浏览器下载），这里只给 URL —— 跟客户导出一个口径 */
  orderImportTemplateUrl: () => '/api/v1/crm/order-import/template',
  orderImportDryRun: (csv: string) =>
    crmFetch<OrderImportDryRun>('/api/v1/crm/order-import/dry-run', {
      method: 'POST',
      body: { csv },
    }),
  orderImportCommit: (csv: string) =>
    crmFetch<{ ok: boolean; created: number; message?: string; dry_run?: OrderImportDryRun }>(
      '/api/v1/crm/order-import/commit',
      { method: 'POST', body: { csv } },
    ),

  // ── 工厂：一家最多 5 个 WhatsApp 号（09-23）──
  /** 这家的全部号位 + 每个号的未读 / 等我回。顶上账号切换器和「我的 WhatsApp」都读它 */
  waAccounts: () =>
    crmFetch<{
      ok: true;
      max: number;
      accounts: WaAccountView[];
      bound_count: number;
      /** 线路（出口 IP）开通了没有 */
      line_ready?: boolean;
      free_slots: number;
    }>('/api/v1/crm/wa-relay/accounts'),
  /** 绑一个新号：占一个空号位出码，返回号位 id */
  waAccountAdd: () =>
    crmFetch<{ ok: boolean; account_id?: string; status?: string; message?: string }>(
      '/api/v1/crm/wa-relay/accounts',
      { method: 'POST' },
    ),
  waAccountBind: (id: string) =>
    crmFetch<{ ok: boolean; status?: string; reason?: string; message?: string }>(
      `/api/v1/crm/wa-relay/accounts/${encodeURIComponent(id)}/bind`,
      { method: 'POST' },
    ),
  waAccountStatus: (id: string) =>
    crmFetch<{ ok: true; session: WaRelaySessionView | null; live: boolean }>(
      `/api/v1/crm/wa-relay/accounts/${encodeURIComponent(id)}/status`,
    ),
  waAccountQr: (id: string) =>
    crmFetch<{ ok: true; qr: string | null; expires_at?: string; status: string }>(
      `/api/v1/crm/wa-relay/accounts/${encodeURIComponent(id)}/qr`,
    ),
  waAccountUnbind: (id: string) =>
    crmFetch<{ ok: boolean }>(
      `/api/v1/crm/wa-relay/accounts/${encodeURIComponent(id)}/unbind?confirm=1`,
      { method: 'POST' },
    ),
  /** 改这个号的可见范围 / 号主（号主或主账号；换号主只有主账号） */
  waAccountAccess: (
    id: string,
    body: {
      visibility?: 'owner_only' | 'all' | 'members';
      visible_user_ids?: string[];
      owner_user_id?: string;
    },
  ) =>
    crmFetch<{ ok: boolean; message?: string }>(
      `/api/v1/crm/wa-relay/accounts/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    ),
  /** 认领 / 改回公共（工厂子账号和自营业务员同一个入口） */
  portalClaim: (customerId: string, action: 'claim' | 'release') =>
    crmFetch<{ ok: boolean; message?: string }>(
      `/api/v1/crm/portal/conversations/${encodeURIComponent(customerId)}/claim`,
      { method: 'POST', body: { action } },
    ),
  /** 本厂的在用账号（选同事、换号主用） */
  portalMembers: () =>
    crmFetch<{ ok: true; members: PortalMember[] }>('/api/v1/crm/wa-relay/members'),
  waAccountRename: (id: string, label: string | null) =>
    crmFetch<{ ok: boolean; label?: string | null }>(
      `/api/v1/crm/wa-relay/accounts/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: { label } },
    ),

  // ── P3 WhatsApp 扫码托管 ──
  waRelayBind: () =>
    crmFetch<{ ok: boolean; status?: string; reason?: string; message?: string }>(
      '/api/v1/crm/wa-relay/bind',
      { method: 'POST' },
    ),
  waRelayStatus: () =>
    crmFetch<{ ok: true; session: WaRelaySessionView | null; live: boolean }>(
      '/api/v1/crm/wa-relay/status',
    ),
  /** 二维码单独一个端点 —— 拿到它的人能登录那个 WhatsApp 账号，不跟状态混一起 */
  waRelayQr: () =>
    crmFetch<{ ok: true; qr: string | null; expires_at?: string; status: string }>(
      '/api/v1/crm/wa-relay/qr',
    ),
  waRelayUnbind: () =>
    crmFetch<{ ok: boolean }>('/api/v1/crm/wa-relay/unbind?confirm=1', { method: 'POST' }),
  /**
   * 我们自己那一路（sitebox 扫码绑的号）。跟上面四条**同一个页面组件**，
   * 只是打 `/self/*`：内部身份，会话 key 是自营那条租户记录（后端从库里查，不接受参数）。
   */
  waRelaySelfBind: () =>
    crmFetch<{ ok: boolean; status?: string; reason?: string; message?: string }>(
      '/api/v1/crm/wa-relay/self/bind',
      { method: 'POST' },
    ),
  waRelaySelfStatus: () =>
    crmFetch<{ ok: true; session: WaRelaySessionView | null; live: boolean }>(
      '/api/v1/crm/wa-relay/self/status',
    ),
  waRelaySelfQr: () =>
    crmFetch<{ ok: true; qr: string | null; expires_at?: string; status: string }>(
      '/api/v1/crm/wa-relay/self/qr',
    ),
  waRelaySelfUnbind: () =>
    crmFetch<{ ok: boolean }>('/api/v1/crm/wa-relay/self/unbind?confirm=1', { method: 'POST' }),
  /** 出口 IP 池：有几条、各自归谁。配置页靠它做分配决定 */
  waEgressList: () =>
    crmFetch<{
      ok: true;
      entries: Array<{
        tag: string;
        /** 这条出口现在的真实公网 IP（wa-bridge 探的）；分配之前就有值 */
        public_ip: string | null;
        free: boolean;
        holders: Array<{
          tenant_id: string;
          tenant_name: string | null;
          self_operated: boolean;
          status: string | null;
          public_ip: string | null;
        }>;
      }>;
      /** 池子里没有、库里还挂着的 tag（退订残留，要人处理） */
      orphans: string[];
    }>('/api/v1/crm/wa-relay/admin/egress'),
  /** 把一条出口长期指派给一家租户；`egress_tag: null` = 解除 */
  // ── 社媒评论区监听（TikTok，仅内部）──
  socialOverview: () => crmFetch<SocialOverview>('/api/v1/crm/social/overview'),
  socialWatches: () =>
    crmFetch<{ ok: true; entries: SocialWatchEntry[] }>('/api/v1/crm/social/watches'),
  socialWatchCreate: (body: { tenant_id: string; handle: string; max_videos?: number }) =>
    crmFetch<{ ok: true; id: string; handle: string; run_id: string }>(
      '/api/v1/crm/social/watches',
      { method: 'POST', body },
    ),
  socialWatchPatch: (
    id: string,
    body: { enabled?: boolean; max_videos?: number; poll_interval_min?: number },
  ) =>
    crmFetch<{ ok: boolean }>(`/api/v1/crm/social/watches/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
    }),
  socialWatchDelete: (id: string) =>
    crmFetch<{ ok: boolean }>(`/api/v1/crm/social/watches/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  socialWatchRun: (id: string) =>
    crmFetch<{ ok: true; run_id: string; reused: boolean }>(
      `/api/v1/crm/social/watches/${encodeURIComponent(id)}/run`,
      { method: 'POST', body: {} },
    ),
  socialRun: (id: string) =>
    crmFetch<{ ok: true; run: SocialRunEntry }>(
      `/api/v1/crm/social/runs/${encodeURIComponent(id)}`,
    ),
  socialComments: (params: {
    watch_id?: string;
    tenant_id?: string;
    stage?: SocialStage;
    intent?: boolean;
    status?: string;
    q?: string;
    before?: string | null;
  }) => {
    const qs = new URLSearchParams();
    if (params.watch_id) qs.set('watch_id', params.watch_id);
    if (params.tenant_id) qs.set('tenant_id', params.tenant_id);
    if (params.stage) qs.set('stage', params.stage);
    if (params.intent) qs.set('intent', '1');
    if (params.status) qs.set('status', params.status);
    if (params.q) qs.set('q', params.q);
    if (params.before) qs.set('before', params.before);
    return crmFetch<{ ok: true; entries: SocialCommentEntry[]; next: string | null }>(
      `/api/v1/crm/social/comments?${qs.toString()}`,
    );
  },
  // ── 工厂端「社媒线索」：只有工厂看得懂的状态，没有出口 / 日志 ──
  portalSocial: () =>
    crmFetch<{
      ok: true;
      line_ready: boolean;
      /** 我们配没配 YouTube 接口 */
      youtube_ready?: boolean;
      max: number;
      /** 等你回复的条数 */
      pending: number;
      accounts: PortalSocialAccount[];
    }>('/api/v1/crm/social/mine/'),
  portalSocialAdd: (handle: string, platform: 'tiktok' | 'youtube' = 'tiktok') =>
    crmFetch<{ ok: true; id: string; handle: string }>('/api/v1/crm/social/mine/accounts', {
      method: 'POST',
      body: { handle, platform },
    }),
  /** Facebook / Instagram 评论直接回 */
  portalSocialReply: (id: string, text: string) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/social/mine/comments/${encodeURIComponent(id)}/reply`, {
      method: 'POST',
      body: { text },
    }),
  // ── 「渠道」：每个渠道接上了没有（工厂端和大后台同一页） ──
  portalChannels: () => crmFetch<PortalChannels>('/api/v1/crm/channels/mine'),
  portalMetaConnect: () =>
    crmFetch<{ ok: true; url: string }>('/api/v1/crm/channels/meta/connect', {
      method: 'POST',
      body: {},
    }),
  portalMetaDisconnect: (pageId: string) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/channels/meta/pages/${encodeURIComponent(pageId)}`, {
      method: 'DELETE',
    }),
  // ── 询盘邮箱自助绑定（11-03）：网站域名开通 / 授权码绑企业邮箱 ──
  portalMailboxes: () => crmFetch<PortalMailboxes>('/api/v1/crm/channels/mailboxes'),
  portalMailboxDetect: (address: string) =>
    crmFetch<{ ok: true; provider: PortalMailboxProvider }>(
      '/api/v1/crm/channels/mailboxes/detect',
      { method: 'POST', body: { address } },
    ),
  portalMailboxBind: (body: {
    address: string;
    password: string;
    display_name?: string | null;
    imap_host?: string;
    imap_port?: number;
    smtp_host?: string;
    smtp_port?: number;
  }) =>
    crmFetch<{ ok: true; item: PortalMailbox }>('/api/v1/crm/channels/mailboxes', {
      method: 'POST',
      body,
    }),
  portalMailboxHosted: (body: { slug: string; local_part: string; display_name?: string | null }) =>
    crmFetch<{ ok: true; address: string; send_ready: boolean; note: string | null }>(
      '/api/v1/crm/channels/mailboxes/hosted',
      { method: 'POST', body },
    ),
  portalMailboxCheck: (id: string, password?: string) =>
    crmFetch<{ ok: true; item: PortalMailbox }>(
      `/api/v1/crm/channels/mailboxes/${encodeURIComponent(id)}/check`,
      { method: 'POST', body: password ? { password } : {} },
    ),
  portalMailboxRemove: (id: string) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/channels/mailboxes/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  channelsOverview: () => crmFetch<ChannelsOverview>('/api/v1/crm/channels/overview'),
  metaSetupWebhooks: () =>
    crmFetch<{ ok: true }>('/api/v1/crm/channels/meta/setup-webhooks', {
      method: 'POST',
      body: {},
    }),
  portalSocialSetEnabled: (id: string, enabled: boolean) =>
    crmFetch<{ ok: boolean }>(`/api/v1/crm/social/mine/accounts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { enabled },
    }),
  portalSocialRemove: (id: string) =>
    crmFetch<{ ok: boolean }>(`/api/v1/crm/social/mine/accounts/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  portalSocialCheck: (id: string) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/social/mine/accounts/${encodeURIComponent(id)}/check`, {
      method: 'POST',
      body: {},
    }),
  portalSocialPending: () =>
    crmFetch<{ ok: true; entries: PortalSocialComment[] }>('/api/v1/crm/social/mine/pending'),
  portalSocialRecent: (before?: string | null) =>
    crmFetch<{ ok: true; entries: PortalSocialComment[]; next: string | null }>(
      `/api/v1/crm/social/mine/recent${before ? `?before=${encodeURIComponent(before)}` : ''}`,
    ),
  portalSocialOpened: (id: string) =>
    crmFetch<{ ok: boolean }>(`/api/v1/crm/social/mine/comments/${encodeURIComponent(id)}/opened`, {
      method: 'POST',
      body: {},
    }),
  portalSocialDismiss: (id: string, undo = false) =>
    crmFetch<{ ok: boolean }>(
      `/api/v1/crm/social/mine/comments/${encodeURIComponent(id)}/dismiss`,
      { method: 'POST', body: { undo } },
    ),
  portalSocialTranslate: (id: string) =>
    crmFetch<{ ok: true; text_zh: string }>(
      `/api/v1/crm/social/mine/comments/${encodeURIComponent(id)}/translate`,
      { method: 'POST', body: {} },
    ),
  socialCommentStatus: (id: string, status: 'new' | 'seen' | 'lead' | 'ignored') =>
    crmFetch<{ ok: boolean }>(`/api/v1/crm/social/comments/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: { status },
    }),
  /**
   * 给这家指定出口 IP（`null` = 收回）。**一家一条，这家所有 WhatsApp 号共用**（09-23 定死）。
   * 这家还有号在用时收回会被后端拒。
   */
  waEgressAssign: (tenantId: string, egressTag: string | null) =>
    crmFetch<{ ok: boolean; egress_tag?: string | null; message?: string }>(
      '/api/v1/crm/wa-relay/admin/egress/assign',
      // crmFetch 内部会 stringify —— 这里传对象。外面再 stringify 一次的话
      // 服务端收到的是一个 JSON 字符串而不是对象，字段全读不到，表现成 400。
      { method: 'POST', body: { tenant_id: tenantId, egress_tag: egressTag } },
    ),
  waRelayAdminSessions: () =>
    crmFetch<{
      ok: true;
      bridge_reachable: boolean;
      /** wa-bridge 进程内存 ÷ 活跃会话 —— 扩容判据，问不到是 null */
      capacity?: {
        active?: number | null;
        rss_mb?: number;
        heap_used_mb?: number;
        per_session_mb?: number | null;
      } | null;
      entries: WaRelayAdminEntry[];
    }>('/api/v1/crm/wa-relay/admin/sessions'),

  // ── D3 物流轨迹（读 + 手工填报；provider 那半等选型）──
  getOrderTracking: (id: string) =>
    crmFetch<OrderTracking>(`/api/v1/crm/orders/${encodeURIComponent(id)}/tracking`),
  addOrderTracking: (
    id: string,
    body: {
      ts: string;
      status: string;
      location?: string | null;
      description?: string | null;
      carrier?: string | null;
      tracking_no?: string | null;
    },
  ) =>
    crmFetch<{ ok: true; id: string; last_tracking_at: string }>(
      `/api/v1/crm/orders/${encodeURIComponent(id)}/tracking`,
      { method: 'POST', body },
    ),

  // ── P2 客户资产池 ──
  customerTimeline: (id: string, q: { types?: string; before?: string; limit?: number } = {}) =>
    crmFetch<{
      ok: true;
      entries: TimelineEntry[];
      next_before: string | null;
      available_types: string[];
    }>(`/api/v1/crm/customers/${encodeURIComponent(id)}/timeline${buildQuery(q)}`),
  addCustomerNote: (id: string, body: { text: string; actor_name?: string }) =>
    crmFetch<{ ok: true; event_id: string | null }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/notes`,
      { method: 'POST', body },
    ),
  mergeCandidates: (id: string) =>
    crmFetch<{
      ok: true;
      target: { customer_id: string; display_name: string | null };
      candidates: MergeCandidateEntry[];
    }>(`/api/v1/crm/customers/${encodeURIComponent(id)}/merge-candidates`),
  mergePreview: (id: string, sourceId: string) =>
    crmFetch<{ ok: true; counts: Record<string, number>; total: number }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/merge-preview?source_id=${encodeURIComponent(sourceId)}`,
    ),
  mergeCustomer: (id: string, sourceId: string) =>
    crmFetch<{ ok: true; moved: Record<string, number>; total: number }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/merge`,
      { method: 'POST', body: { source_id: sourceId } },
    ),
  /** 导出走原生跳转（要走浏览器下载），这里只给 URL */
  customerExportUrl: (q: { status?: string; country?: string; q?: string } = {}) =>
    `/api/v1/crm/customers/export${buildQuery(q)}`,

  // ── P2 漏项：跟进与状态管理 ──
  /** 阶段 / 负责人 / 下次跟进。阶段选项跟着接口下发，前端不硬编码那六档 */
  customerFollowUp: (id: string) =>
    crmFetch<{ ok: true; stages: StageOption[] } & FollowUpSummary>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/follow-up`,
    ),
  setCustomerStage: (id: string, body: { stage: string; note?: string; actor_name?: string }) =>
    crmFetch<{ ok: true; from: string; to: string }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/stage`,
      { method: 'PATCH', body },
    ),
  addCustomerFollowUp: (
    id: string,
    body: { text: string; next_at?: string; next_note?: string; actor_name?: string },
  ) =>
    crmFetch<{ ok: true; event_id: string | null; next_follow_up_at: string | null }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/follow-ups`,
      { method: 'POST', body },
    ),
  setNextFollowUp: (
    id: string,
    body: { next_at: string | null; next_note?: string | null; actor_name?: string },
  ) =>
    crmFetch<{ ok: true; next_follow_up_at: string | null }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/next-follow-up`,
      { method: 'PATCH', body },
    ),
  setCustomerOwner: (id: string, body: { owner_user_id: string | null; actor_name?: string }) =>
    crmFetch<{ ok: true; owner_user_id: string | null; owner_name: string | null }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/owner`,
      { method: 'PATCH', body },
    ),

  // ── P2 漏项：报价 ──
  listCustomerQuotes: (id: string) =>
    crmFetch<{ ok: true; entries: QuoteEntry[] }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/quotes`,
    ),
  createQuote: (
    id: string,
    body: {
      items: Array<{
        factory_sku?: string | null;
        title?: string | null;
        quantity: number;
        unit_price: number;
      }>;
      currency?: string;
      valid_until?: string;
      actor_name?: string;
    },
  ) =>
    crmFetch<{ ok: true; quote: QuoteEntry }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/quotes`,
      { method: 'POST', body },
    ),
  /** 发 = 落一份草稿等人过目，**不直发**（后端 quote-service 里写了理由） */
  sendQuote: (quoteId: string, actorName?: string) =>
    crmFetch<{ ok: true; draft_id: string; text: string }>(
      `/api/v1/crm/quotes/${encodeURIComponent(quoteId)}/send`,
      { method: 'POST', body: { actor_name: actorName } },
    ),
  /** 测试付款开关（10-23）：这份报价走 Stripe 沙盒，不扣真钱。只有内部账号能切 */
  setQuotePaymentTest: (quoteId: string, on: boolean) =>
    crmFetch<{ ok: true; test: boolean }>(
      `/api/v1/crm/quotes/${encodeURIComponent(quoteId)}/payment-test`,
      { method: 'POST', body: { on } },
    ),

  convertQuote: (
    quoteId: string,
    body: { po_number: string; supplier_id: string; flow_key?: string; actor_name?: string },
  ) =>
    crmFetch<{ ok: true; order_id: string; po_number: string }>(
      `/api/v1/crm/quotes/${encodeURIComponent(quoteId)}/convert`,
      { method: 'POST', body },
    ),

  // ── 租户通知（工厂端设置 + 内部送达情况）──
  notifySettings: () =>
    crmFetch<{
      ok: true;
      notify: TenantNotifySettings | null;
      tenant_name: string | null;
      login_wechat_available: boolean;
    }>('/api/v1/crm/tenant-notify/settings'),
  saveNotifySettings: (body: TenantNotifySettings) =>
    crmFetch<{ ok: true; notify: TenantNotifySettings }>('/api/v1/crm/tenant-notify/settings', {
      method: 'PUT',
      body,
    }),
  /** 配完当场发一条：webhook 贴错一个字符，只有这样才发现得了 */
  testNotify: () =>
    crmFetch<{ ok: boolean; message?: string }>('/api/v1/crm/tenant-notify/test', {
      method: 'POST',
      body: {},
    }),
  notifyLogs: (q: { status?: string; tenant_id?: string; limit?: number } = {}) =>
    crmFetch<{ ok: true; entries: TenantNotificationEntry[] }>(
      `/api/v1/crm/tenant-notify/logs${buildQuery(q)}`,
    ),

  // ── 免费联营派单（G3/G4 补做）──
  /** 候选工厂：C5 撮合建议 + 按名字搜。没开通账号 / 没绑微信的照样列，前端标出来 */
  assignmentCandidates: (q: { customer_id?: string; q?: string } = {}) =>
    crmFetch<{ ok: true; candidates: FactoryCandidate[] }>(
      `/api/v1/crm/assignments/candidates${buildQuery(q)}`,
    ),
  /** 这个客户派给了谁（含已撤的，留痕不删） */
  assignments: (customerId: string) =>
    crmFetch<{ ok: true; entries: AssignmentEntry[] }>(
      `/api/v1/crm/assignments?customer_id=${encodeURIComponent(customerId)}`,
    ),
  assignFactory: (body: {
    customer_id: string;
    tenant_id: string;
    skus: TopicSku[];
    context_event_ids: string[];
    note?: string;
  }) =>
    crmFetch<{ ok: true; assignment_id: string; created: boolean; tenant_name: string }>(
      '/api/v1/crm/assignments',
      { method: 'POST', body: { ...body, source: 'admin' } },
    ),
  /** 转单第一步：这个客户提过的货（询价单 / 报价 / 聊天 / 撮合），在别家手里的带 held_by */
  assignmentSkuOptions: (customerId: string) =>
    crmFetch<{ ok: true; options: SkuOption[] }>(
      `/api/v1/crm/assignments/sku-options?customer_id=${encodeURIComponent(customerId)}`,
    ),
  /** 搜货的默认搜索词（从这个客户最近的消息里抽） */
  goodsSuggest: (customerId: string) =>
    crmFetch<{ ok: true; query: string }>(
      `/api/v1/crm/assignments/goods-suggest?customer_id=${encodeURIComponent(customerId)}`,
    ),
  /** 搜货：全部货盘里找，按工厂分组 */
  goodsSearch: (q: string, customerId: string) =>
    crmFetch<{
      ok: true;
      keywords: string[];
      factories: GoodsFactory[];
      totals?: { onboarded: number; unopened: number };
    }>(`/api/v1/crm/assignments/goods-search${buildQuery({ q, customer_id: customerId })}`),
  /** 转单第二步：消息清单，suggested = 跟所选货有关（默认勾上） */
  assignmentContextOptions: (customerId: string, skuKeys: string[]) =>
    crmFetch<{ ok: true; options: ContextOption[] }>(
      `/api/v1/crm/assignments/context-options${buildQuery({
        customer_id: customerId,
        sku_keys: skuKeys.join(','),
      })}`,
    ),
  /** 话题里的来回（后台点进去看） */
  topicEvents: (assignmentId: string) =>
    crmFetch<{ ok: true; events: Array<Record<string, unknown>> }>(
      `/api/v1/crm/assignments/${encodeURIComponent(assignmentId)}/events`,
    ),
  /** 勾几条一次推给这个话题 */
  pushToTopic: (assignmentId: string, eventIds: string[]) =>
    crmFetch<{ ok: boolean; pushed: number; message?: string }>(
      `/api/v1/crm/assignments/${encodeURIComponent(assignmentId)}/push`,
      { method: 'POST', body: { event_ids: eventIds } },
    ),
  /** 这个客户每条消息推没推、推给了谁 */
  assignmentRoutings: (customerId: string) =>
    crmFetch<{ ok: true; entries: RelayRoutingEntry[] }>(
      `/api/v1/crm/assignments/routings?customer_id=${encodeURIComponent(customerId)}`,
    ),
  /** 撤回一次推送；带 moveTo 就是改推 */
  undoRouting: (routingId: string, moveTo?: string | null) =>
    crmFetch<{ ok: boolean; message?: string }>(
      `/api/v1/crm/assignments/routings/${encodeURIComponent(routingId)}/undo`,
      { method: 'POST', body: { move_to: moveTo ?? null } },
    ),
  /** 判不出的那几条：自己回 */
  dismissRoutings: (eventIds: string[]) =>
    crmFetch<{ ok: true }>('/api/v1/crm/assignments/routings/dismiss', {
      method: 'POST',
      body: { event_ids: eventIds },
    }),
  relayAccuracy: (days = 30) =>
    crmFetch<{ ok: true } & RelayAccuracy>(`/api/v1/crm/assignments/accuracy?days=${days}`),
  revokeAssignment: (id: string, reason?: string) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/assignments/${encodeURIComponent(id)}/revoke`, {
      method: 'POST',
      body: { reason },
    }),
  /** 工厂端：「这单我做不了」—— 软性反馈，授权不会因此撤销 */
  portalFeedback: (customerId: string, reason: string) =>
    crmFetch<{ ok: boolean; message?: string }>(
      `/api/v1/crm/portal/conversations/${encodeURIComponent(customerId)}/feedback`,
      { method: 'POST', body: { reason } },
    ),

  // ── G2 工厂身份绑定 ──
  bindRequests: (status = 'pending') =>
    crmFetch<{ ok: true; entries: BindRequestEntry[] }>(
      `/api/v1/crm/tenant-bind/requests?status=${encodeURIComponent(status)}`,
    ),
  approveBind: (
    id: string,
    /** supplier_id 不给就现建一家（用 supplier_name，缺省取对方填的公司名） */
    body: {
      supplier_id?: string;
      supplier_name?: string;
      tier?: 'free' | 'pro' | 'managed';
      tenant_slug?: string;
      contact_name?: string;
    },
  ) =>
    crmFetch<{ ok: true; tenant_id: string }>(
      `/api/v1/crm/tenant-bind/requests/${encodeURIComponent(id)}/approve`,
      { method: 'POST', body },
    ),
  rejectBind: (id: string, reason: string) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/tenant-bind/requests/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: { reason },
    }),
  bindDashboard: () => crmFetch<BindDashboard>('/api/v1/crm/tenant-bind/dashboard'),

  // ── G4 工厂端 ──
  portalSummary: () => crmFetch<PortalSummary>('/api/v1/crm/portal/summary'),
  portalConversation: (customerId: string) =>
    crmFetch<PortalConversation>(
      `/api/v1/crm/portal/conversations/${encodeURIComponent(customerId)}`,
    ),

  sourcingTasks: (q: { limit?: number; status?: string } = {}) =>
    crmFetch<{ ok: true; tasks: BpSourcingTaskEntry[] }>(
      `/api/v1/crm/sourcing/tasks${buildQuery(q)}`,
    ),
  sourcingTask: (id: string) =>
    crmFetch<{ ok: true; task: BpSourcingTaskEntry; candidates: BpSourcingCandidateEntry[] }>(
      `/api/v1/crm/sourcing/tasks/${encodeURIComponent(id)}`,
    ),
  /** 删一条失败的寻源记录(后端只允许删 failed) */
  deleteSourcingTask: (id: string) =>
    crmFetch<{ ok: true; deleted: number }>(
      `/api/v1/crm/sourcing/tasks/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  /** 一键清空所有失败记录 */
  clearFailedSourcingTasks: () =>
    crmFetch<{ ok: true; deleted: number }>('/api/v1/crm/sourcing/tasks?status=failed', {
      method: 'DELETE',
    }),
  startSourcing: (body: { q: string; filter?: string; maxProducts?: number }) =>
    crmFetch<{
      ok: true;
      task_id: string;
      api_calls: number;
      candidates: BpSourcingCandidateEntry[];
    }>('/api/v1/crm/sourcing', { method: 'POST', body: { ...body, trigger: 'admin' } }),
  /**
   * SSE 版寻源 —— admin 弹窗调用,从点击瞬间起各阶段实时 fire 事件(日志感)。
   * 返回 Promise<void>,流结束时 resolve;onEvent 拿每条 SSE 帧;abortSignal 取消则中断。
   * 与同步 startSourcing 同一后端逻辑、同一成本红线,只是渐进式渲染。
   */
  startSourcingStream: async (
    body: { q: string; filter?: string; maxProducts?: number },
    onEvent: (evt: BpSourcingStreamEvent) => void,
    abortSignal?: AbortSignal,
  ): Promise<void> => {
    const init: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-Crm-Module': 'admin',
      },
      body: JSON.stringify({ ...body, trigger: 'admin' }),
    };
    if (abortSignal) init.signal = abortSignal;

    const res = await fetch('/api/v1/crm/sourcing/stream', init);
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');
      throw new CrmApiError(
        res.status,
        text,
        `sourcing stream ${res.status}: ${text.slice(0, 200)}`,
      );
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      while (true) {
        const idx = buf.indexOf('\n\n');
        if (idx < 0) break;
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            onEvent(JSON.parse(line.slice(6)) as BpSourcingStreamEvent);
          } catch {
            /* 半包/心跳注释行忽略 */
          }
        }
      }
    }
  },
  listInbound: (q: ListQuery = {}) =>
    crmFetch<CursorListResponse<InboundMessageEntry>>(
      `/api/v1/crm/inbound-messages${buildQuery(q)}`,
    ),
  getEvent: (id: string) =>
    crmFetch<CursorListResponse<InboundMessageEntry>>(
      `/api/v1/crm/customer-events?customer_id=${encodeURIComponent(id)}&limit=50`,
    ),
  listCustomerEvents: (q: ListQuery = {}) =>
    crmFetch<CursorListResponse<InboundMessageEntry>>(
      `/api/v1/crm/customer-events${buildQuery(q)}`,
    ),
  listAgentDecisions: (q: ListQuery = {}) =>
    crmFetch<CursorListResponse<AgentDecisionEntry>>(`/api/v1/crm/agent-decisions${buildQuery(q)}`),
  listDrafts: (q: ListQuery = {}) =>
    crmFetch<CursorListResponse<DraftEntry>>(`/api/v1/crm/drafts${buildQuery(q)}`),
  listDraftSites: () => crmFetch<{ ok: true; sites: DraftSiteEntry[] }>('/api/v1/crm/drafts/sites'),
  listLeads: (q: ListQuery = {}) =>
    crmFetch<CursorListResponse<LeadEntry>>(`/api/v1/crm/leads${buildQuery(q)}`),
  /**
   * 客户都是从我们哪些号 / 邮箱进来的 —— 客户与询盘页顶上那排筛选用它。
   * 选项从真实事件里长出来，不是号码注册表（扫码绑的号不会进注册表）。
   */
  listInboundAccounts: () =>
    crmFetch<{
      ok: true;
      entries: InboundAccount[];
    }>('/api/v1/crm/leads/inbound-accounts'),
  /** 认领（公共池）：把这个客户设成我在跟。已经有人跟的会回 409 */
  claimCustomer: (id: string) =>
    crmFetch<{ ok: boolean; assignee_id?: string; message?: string }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/claim`,
      { method: 'POST' },
    ),
  /** 转交 / 释放回公共池（`salesperson_id: null`）。只有当前负责人和管理员能用 */
  assignCustomer: (id: string, salespersonId: string | null) =>
    crmFetch<{ ok: boolean; assignee_id?: string | null; message?: string }>(
      `/api/v1/crm/customers/${encodeURIComponent(id)}/assign`,
      // 同上：crmFetch 内部 stringify，这里传对象
      { method: 'POST', body: { salesperson_id: salespersonId } },
    ),
  listOutboundSends: (q: ListQuery = {}) =>
    crmFetch<CursorListResponse<OutboundSendEntry>>(`/api/v1/crm/outbound-sends${buildQuery(q)}`),
  listPushEvents: (q: ListQuery = {}) =>
    crmFetch<CursorListResponse<PushEventEntry>>(`/api/v1/crm/push-events${buildQuery(q)}`),
  dashboardHealth: () =>
    crmFetch<{
      ok: true;
      generated_at: string;
      today: {
        inquiries: number;
        claimed: number;
        claim_rate: number | null;
        drafts_generated: number;
        drafts_sent: number;
        ai_direct_send_rate: number | null;
      };
      sla: { violations_l1: number; violations_l2: number };
      stuck_cases: Array<{
        customer_event_id: string;
        customer_id: string;
        customer_display_name: string | null;
        customer_country: string | null;
        channel: string | null;
        text_excerpt: string;
        ts: string | null;
      }>;
    }>('/api/v1/crm/dashboard/health'),
  getDraft: (id: string) =>
    crmFetch<{ ok: true; draft: DraftEntry & Record<string, unknown> }>(
      `/api/v1/crm/drafts/${encodeURIComponent(id)}`,
    ),
  applyDraftDecision: (payload: {
    draft_id: string;
    by: string;
    trace_id?: string | null;
    action: 'send' | 'discard' | 'claim' | 'edit' | 'transfer';
    edited_body?: string;
    transferred_to?: string;
    transferred_to_name?: string;
  }) =>
    crmFetch<{
      ok: true;
      prev_action: string | null;
      new_action: string;
      already_applied?: boolean;
    }>('/api/v1/draft_decision/apply', { method: 'POST', body: payload }),
  /** 手打一条直接发给客户（不经 AI）—— 后台版的「主动发送」 */
  proactiveSend: (payload: {
    customer_id: string;
    by: string;
    text: string;
    /** 随消息发的文件（先用 uploadOutboundFile 传上去）；带文件时 text 可以为空 */
    attachment?: OutboundFileAttachment | null;
    /** 引用回复：引用这个客户的哪一条消息（会话里那条的 _id） */
    quote_event_id?: string | null;
    /** 'email'：官方号过了 24 小时，改发到客户邮箱 */
    via?: 'email' | null;
  }) =>
    crmFetch<{ ok: boolean; channel?: string; reason?: string; message?: string }>(
      '/api/v1/proactive-send',
      { method: 'POST', body: payload },
    ),
  /** 撤回我们发出去的一条（只有扫码号能撤，发出 2 天内） */
  revokeMessage: (payload: { customer_id: string; event_id: string; by: string }) =>
    crmFetch<{ ok: boolean; reason?: string; message?: string }>('/api/v1/proactive-send/revoke', {
      method: 'POST',
      body: payload,
    }),
  /** 上传一个要发给客户的文件，带进度。成功返回附件描述，交给 proactiveSend 发出去 */
  uploadOutboundFile: uploadOutboundFile,
  /**
   * 回复润色（SSE）：业务员写的中文意图 → 结合上下文润色 + 翻译成客户语言。
   * onEvent 逐帧拿 stage / done / error；abortSignal 取消。
   */
  polishReplyStream: (
    customerId: string,
    text: string,
    onEvent: (evt: PolishStreamEvent) => void,
    abortSignal?: AbortSignal,
  ): Promise<void> =>
    readSseStream(
      `/api/v1/crm/cases/${encodeURIComponent(customerId)}/polish`,
      { text },
      onEvent,
      abortSignal,
    ),

  /* ── H2 工厂端 AI 能力 ─────────────────────────────────────────
     工厂端**不打 `/cases/*`**（那是内部路由树）。后端在 portal 下开了同名端点
     转调同一个 service，所以这里也是另一条地址、同一套事件格式。 */

  /** 工厂端润色（SSE，三段进度）。后端调的是内部那条一模一样的 service */
  portalPolishStream: (
    customerId: string,
    text: string,
    onEvent: (evt: PolishStreamEvent) => void,
    abortSignal?: AbortSignal,
  ): Promise<void> =>
    readSseStream(
      `/api/v1/crm/portal/conversations/${encodeURIComponent(customerId)}/polish`,
      { text },
      onEvent,
      abortSignal,
    ),

  /** 翻译成客户的语言。失败返回 ok:false —— 前端保留原文，不清空输入框 */
  /**
   * 按需起草（09-23）：工厂的客户不再自动起草，点「AI 草稿」才调模型。
   * 后台起草、立刻返回（status=generating），前端轮询会话接口等 pending_draft 出来。
   */
  /** redraft=true 是「换一套」：instruction 可不填（不填就换个说法） */
  portalRequestDraft: (customerId: string, opts?: { redraft?: boolean; instruction?: string }) =>
    crmFetch<{
      ok: boolean;
      status?: 'ready' | 'generating' | 'redrafting';
      /** 重写时：会话里草稿的 redraft_count 到这个数才是新稿 */
      expect_redraft_count?: number;
      remaining?: number;
      reason?: string;
      message?: string;
    }>(`/api/v1/crm/portal/conversations/${encodeURIComponent(customerId)}/draft`, {
      method: 'POST',
      body: opts ?? {},
    }),
  portalTranslate: (customerId: string, text: string) =>
    crmFetch<
      | { ok: true; text: string; target_language: string }
      | { ok: false; reason: string; message: string }
    >(`/api/v1/crm/portal/conversations/${encodeURIComponent(customerId)}/translate`, {
      method: 'POST',
      body: { text },
    }),

  /** 找货：拿会话上下文当场撮合一次 */
  portalMatch: (customerId: string, inquiryText: string) =>
    crmFetch<
      | { ok: true; suggested_products: SuggestedProduct[] }
      | { ok: false; reason: string; message: string }
    >(`/api/v1/crm/portal/conversations/${encodeURIComponent(customerId)}/match`, {
      method: 'POST',
      body: { inquiry_text: inquiryText },
    }),

  /** 找供应链：提需求 */
  portalCreateSourcingRequest: (body: {
    need: string;
    quantity?: number | null;
    target_price_cny?: number | null;
    lead_time?: string | null;
    customer_id?: string | null;
  }) =>
    crmFetch<{ ok: true; request: PortalSourcingRequest }>('/api/v1/crm/portal/sourcing-requests', {
      method: 'POST',
      body,
    }),
  portalSourcingRequests: (limit = 20) =>
    crmFetch<{ ok: true; entries: PortalSourcingRequest[] }>(
      `/api/v1/crm/portal/sourcing-requests?limit=${limit}`,
    ),
  /** 「要这家的报价」。传的是**位次**不是 supplier_id —— 工厂那边根本没有 id */
  portalRequestQuote: (requestId: string, rank: number) =>
    crmFetch<{ ok: true; message: string }>(
      `/api/v1/crm/portal/sourcing-requests/${encodeURIComponent(requestId)}/quote`,
      { method: 'POST', body: { rank } },
    ),

  /** 草稿「不用」→ 记 rejected。「用这条」不走接口（填进输入框即可） */
  portalRejectDraft: (customerId: string, draftId: string) =>
    crmFetch<{ ok: true }>(
      `/api/v1/crm/portal/conversations/${encodeURIComponent(customerId)}/draft-feedback`,
      { method: 'POST', body: { draft_id: draftId } },
    ),

  portalAutoReply: () =>
    crmFetch<{ ok: true; auto_reply: PortalAutoReply; ai_draft: boolean }>(
      '/api/v1/crm/portal/auto-reply',
    ),
  savePortalAutoReply: (body: PortalAutoReply) =>
    crmFetch<{ ok: true; auto_reply: PortalAutoReply }>('/api/v1/crm/portal/auto-reply', {
      method: 'PUT',
      body,
    }),

  /** 内部侧：工厂发起的寻源（看得到真厂名，租户身份 403） */
  internalSourcingRequests: (limit = 30) =>
    crmFetch<{ ok: true; entries: InternalSourcingRequest[] }>(
      `/api/v1/crm/sourcing-requests?limit=${limit}`,
    ),
  dispatchSourcingRequest: (id: string, rank: number) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/sourcing-requests/${encodeURIComponent(id)}/dispatch`, {
      method: 'POST',
      body: { rank },
    }),
  /**
   * 站点询盘 → 现在是哪个客户。飞书卡片按钮的跳板用（见 InquiryRedirectPage）。
   * `customer_id` 为 null = 还没入库（异步链，通常几秒），不是错误。
   */
  customerOfInquiry: (inquiryId: string) =>
    crmFetch<{
      ok: true;
      customer_id: string | null;
      site_slug: string | null;
      tenant_id?: string | null;
    }>(`/api/v1/crm/inquiries/${encodeURIComponent(inquiryId)}/customer`),

  /**
   * 把一条消息翻成简中。**业务员在气泡上点一下才调** —— 花钱的调用，
   * 不进任何默认流程。已经有译文的直接回缓存，点两下不花两次钱。
   */
  translateEvent: (eventId: string) =>
    crmFetch<{ ok: true; text_excerpt_zh: string; cached: boolean }>(
      `/api/v1/crm/customer-events/${encodeURIComponent(eventId)}/translate`,
      { method: 'POST' },
    ),

  /**
   * 改客户档案（公司 / 国家 / 语种）。
   * **没传的字段不动，传 null 是清空** —— 两者在接口层就分开了。
   */
  updateCustomerProfile: (
    customerId: string,
    patch: {
      organization?: string | null;
      country?: string | null;
      preferred_lang?: string | null;
      actor_name?: string;
    },
  ) =>
    crmFetch<{
      ok: true;
      changed: Array<{ key: string; label: string; from: string | null; to: string | null }>;
    }>(`/api/v1/crm/customers/${encodeURIComponent(customerId)}/profile`, {
      method: 'PATCH',
      body: patch,
    }),

  /** 工厂端「我的 → AI 用量」：本月按工厂整体额度算的百分比 + 成员占比 */
  portalLlmUsage: () =>
    crmFetch<{
      ok: true;
      month: string;
      unlimited: boolean;
      used_percent: number | null;
      paused: boolean;
      members: Array<{ name: string; share_percent: number }>;
    }>('/api/v1/crm/portal/llm-usage'),

  /** 工厂端「网站 → 产品」：自己名下的货（只读） */
  portalProducts: (q: { q?: string; page?: number } = {}) =>
    crmFetch<{
      ok: true;
      total: number;
      page: number;
      page_size: number;
      products: Array<{
        id: string;
        sku: string;
        title: string | null;
        title_en: string | null;
        price_cny: number | null;
        moq: number | null;
        has_image: boolean;
        sites: string[];
      }>;
    }>(`/api/v1/crm/portal/products${buildQuery(q)}`),

  /* ── 10-22 工厂端 CRM：手工建客户 / 导入 / 补联系方式 / 样品 ── */

  createCustomer: (body: NewCustomerInput) =>
    crmFetch<{ ok: true; id: string }>('/api/v1/crm/customers', { method: 'POST', body }),

  customerImportTemplateUrl: () => '/api/v1/crm/customers/import/template',

  /** 导入页的「填写说明」，跟样表第二页同一份 */
  customerImportGuide: () =>
    crmFetch<{
      ok: true;
      max_rows: number;
      columns: Array<{ column: string; required: string; how: string; example: string }>;
    }>('/api/v1/crm/customers/import/guide'),

  importCustomersDryRun: (file_base64: string, filename: string, mapping?: CustomerImportMapping) =>
    crmFetch<CustomerImportPlan>('/api/v1/crm/customers/import/dry-run', {
      method: 'POST',
      body: { file_base64, filename, ...(mapping ? { mapping } : {}) },
    }),

  importCustomersCommit: (
    file_base64: string,
    filename: string,
    assign_to_me: boolean,
    mapping: CustomerImportMapping,
  ) =>
    crmFetch<{
      ok: true;
      created: number;
      updated: number;
      skipped: number;
      results: CustomerImportRow[];
    }>('/api/v1/crm/customers/import/commit', {
      method: 'POST',
      body: { file_base64, filename, assign_to_me, mapping },
    }),

  getCustomerContact: (customerId: string) =>
    crmFetch<{
      ok: true;
      customer: {
        display_name: string | null;
        organization: string | null;
        country: string | null;
        preferred_lang: string | null;
        emails: string[];
        phones: string[];
        wa_ids: string[];
      };
    }>(`/api/v1/crm/customers/${encodeURIComponent(customerId)}/contact`),

  /** 改名字、补联系方式（只加不删） */
  addCustomerContact: (
    customerId: string,
    body: { display_name?: string; emails?: string[]; phones?: string[]; wa_ids?: string[] },
  ) =>
    crmFetch<{ ok: true; changed: string[] }>(
      `/api/v1/crm/customers/${encodeURIComponent(customerId)}/contact`,
      { method: 'PATCH', body },
    ),

  listCustomerSamples: (customerId: string) =>
    crmFetch<{
      ok: true;
      statuses: Array<{ key: SampleStatusKey; label: string }>;
      samples: CustomerSampleView[];
    }>(`/api/v1/crm/customers/${encodeURIComponent(customerId)}/samples`),

  createCustomerSample: (customerId: string, body: CustomerSampleInput) =>
    crmFetch<{ ok: true; sample: CustomerSampleView }>(
      `/api/v1/crm/customers/${encodeURIComponent(customerId)}/samples`,
      { method: 'POST', body },
    ),

  patchCustomerSample: (sampleId: string, body: Partial<CustomerSampleInput>) =>
    crmFetch<{ ok: true; sample: CustomerSampleView }>(
      `/api/v1/crm/customers/samples/${encodeURIComponent(sampleId)}`,
      { method: 'PATCH', body },
    ),

  /** 话术库：快捷回复和自动回复共用这一张表 */
  listReplyTemplates: (usage?: 'quick_reply' | 'auto_reply') =>
    crmFetch<{
      ok: true;
      entries: Array<{
        _id: string;
        key: string;
        title: string;
        body: string;
        lang: string | null;
        scene: string;
        usage: string[];
        sort_weight: number;
      }>;
    }>(`/api/v1/crm/reply-templates${usage ? `?usage=${usage}` : ''}`),

  getCase: (customerId: string) =>
    crmFetch<{
      ok: true;
      customer: {
        _id: string;
        display_name: string | null;
        organization: string | null;
        country: string | null;
        country_label?: string | null;
        emails: string[];
        phones: string[];
        status: string;
        intent_tier: string | null;
        first_seen_at: string | null;
        last_seen_at: string | null;
        locale?: CustomerLocale | null;
        preferred_lang?: string | null;
        channels_seen?: string[];
        /** 最早一条入站 = 这个客户哪来的（渠道 / 站点 / 哪个号） */
        origin?: {
          channel: string | null;
          channel_label: string | null;
          site_slug: string | null;
          endpoint_label: string | null;
          at: string | null;
        } | null;
        /** 这条回复会从我们哪个账号发出去（判据跟真发信是同一个 inbound_to） */
        reply_from?: {
          channel: string | null;
          account: string | null;
          account_label: string | null;
          text: string | null;
        } | null;
      };
      assignee: {
        _id: string;
        name: string;
        feishu_open_id: string;
        role: string;
      } | null;
      stats: {
        inbound_events: number;
        decisions: number;
        drafts: number;
        pushes: number;
        /** 我方最后一次回复时间；null = 从未回复过 */
        last_replied_at?: string | null;
        /** 客户最后一条来信 */
        last_inbound_at?: string | null;
        /** WhatsApp 24h 窗口关闭时刻 —— 过了就发不出自由文本了。托管腿和邮件下为 null */
        reply_window_closes_at?: string | null;
        /**
         * 带原因的窗口。`reason`：
         *   `wa_relay_no_window`  扫码托管的号，没有 24 小时限制
         *   `wa_relay_offline`    那个号掉线了，要去重新扫码（不是等客户说话）
         */
        reply_window?: {
          closes_at: string | null;
          closed: boolean;
          remaining_ms: number | null;
          reason?: string | null;
        } | null;
      };
      timeline: Array<
        Record<string, unknown> & {
          kind: 'event' | 'decision' | 'draft' | 'push';
          ts: string;
        }
      >;
      /** 最近几条「没发出去」：界面说已发送、实际被拒或压根没发的那些 */
      send_failures?: Array<{
        _id: string;
        ts: string;
        /** skipped = 发之前就判定发不出去（连气泡都没有）；failed = 发了被拒 */
        kind: 'failed' | 'skipped';
        reason: string;
        channel: string | null;
        permanent: boolean;
        message_id: string | null;
        draft_id: string | null;
      }>;
    }>(`/api/v1/crm/cases/${encodeURIComponent(customerId)}`),
  listSalespeople: (q: { role?: string; active?: 'true' | 'false'; limit?: number } = {}) =>
    crmFetch<{
      ok: true;
      entries: Array<{
        _id: string;
        feishu_open_id: string;
        name: string;
        email: string | null;
        role: string;
        team: string | null;
        active: boolean;
        supervisor_id: string | null;
        responsible_regions: string[];
        responsible_langs: string[];
        joined_at: string | null;
        current_assigned: number;
      }>;
    }>(`/api/v1/salespeople${buildQuery(q as ListQuery)}`),
  simulateInbound: (payload: {
    source?: string;
    display_name?: string;
    emails?: string[];
    message?: string;
    organization?: string;
    country?: string;
  }) =>
    crmFetch<{
      ok: true;
      simulated: true;
      action: string;
      customer_id?: string;
      event_id?: string;
    }>('/api/v1/crm/_test/simulate-inbound', { method: 'POST', body: payload }),
  runPipeline: (payload: { event_id: string }) =>
    crmFetch<{
      ok: boolean;
      stage?: string;
      skipped?: string;
      already_done?: boolean;
      intent?: Record<string, unknown>;
      draft?: {
        _id: string;
        draft_body: string;
        draft_body_zh?: string | null;
        draft_language: string;
        draft_confidence: number;
        scene: string;
        status: string;
      };
      llm?: {
        model: string;
        latency_ms: number;
        input_tokens: number;
        output_tokens: number;
      };
      message?: string;
      error?: string;
      latency_ms?: number;
    }>('/api/v1/crm/_test/run-pipeline', { method: 'POST', body: payload }),
  simulatePushCard: (payload: {
    draft_id?: string;
    customer_display_name?: string | null;
    customer_email?: string | null;
    organization?: string | null;
    message_excerpt?: string;
    draft_body?: string | null;
    draft_language?: string | null;
    scene?: string | null;
    status?: 'generated' | 'manual-review' | 'skipped';
    triggered_by?: 'L1' | 'L2' | 'L3' | null;
    trace_id?: string | null;
    recipient_open_id?: string;
  }) =>
    crmFetch<{
      ok: true;
      simulated: true;
      dry_run: true;
      recipient_open_id: string;
      card_input: Record<string, unknown>;
      card: {
        config: { wide_screen_mode: boolean; update_multi: boolean };
        header: { title: { tag: string; content: string }; template: string };
        elements: Array<Record<string, unknown>>;
      };
      manual_steps: string[];
    }>('/api/v1/crm/_test/push-card-simulate', { method: 'POST', body: payload }),
  applyDraftAction: (payload: {
    draft_id: string;
    by: string;
    action: 'send' | 'discard' | 'claim' | 'edit' | 'transfer';
    edited_body?: string;
    transferred_to?: string;
    transferred_to_name?: string;
    trace_id?: string | null;
  }) =>
    crmFetch<{ ok: boolean; already_applied?: boolean; new_action?: string }>(
      '/api/v1/draft_decision/apply',
      { method: 'POST', body: payload },
    ),
  listPushRules: (
    q: { enabled?: 'true' | 'false'; channel?: string; priority_level?: 'A' | 'B' | 'C' } = {},
  ) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') sp.set(k, String(v));
    const s = sp.toString();
    return crmFetch<{ ok: true; entries: PushRuleEntry[] }>(
      `/api/v1/crm/push-rules${s ? `?${s}` : ''}`,
    );
  },
  createPushRule: (rule: PushRuleEntry) =>
    crmFetch<{ ok: true; rule: PushRuleEntry }>('/api/v1/crm/push-rules', {
      method: 'POST',
      body: rule,
    }),
  updatePushRule: (rule_id: string, patch: Partial<PushRuleEntry>) =>
    crmFetch<{ ok: true; rule: PushRuleEntry }>(
      `/api/v1/crm/push-rules/${encodeURIComponent(rule_id)}`,
      { method: 'PUT', body: patch },
    ),
  deletePushRule: (rule_id: string) =>
    crmFetch<{ ok: true; deleted: true }>(`/api/v1/crm/push-rules/${encodeURIComponent(rule_id)}`, {
      method: 'DELETE',
    }),
  // 来源端点注册表（号码/邮箱/域名 → 备注名 + 所属站点）
  listInboundEndpoints: () =>
    crmFetch<{ endpoints: InboundEndpointEntry[] }>('/api/v1/crm/inbound-endpoints'),
  upsertInboundEndpoint: (body: {
    channel: string;
    address: string;
    label: string;
    site_slug?: string | null;
    note?: string | null;
    active?: boolean;
  }) =>
    crmFetch<{ ok: true; endpoint: InboundEndpointEntry }>('/api/v1/crm/inbound-endpoints', {
      method: 'POST',
      body,
    }),
  deleteInboundEndpoint: (id: string) =>
    crmFetch<{ ok: true; matched: number }>(
      `/api/v1/crm/inbound-endpoints/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  listContactBlocks: (customer_id: string) =>
    crmFetch<{ ok: true; customer_id: string; blocks: ContactBlockEntry[] }>(
      `/api/v1/crm/contact-blocks?customer_id=${encodeURIComponent(customer_id)}`,
    ),
  listContactBlocksAudit: (
    q: {
      customer_id?: string;
      email?: string;
      action?: 'add' | 'remove';
      block_type?: 'bounced' | 'complained' | 'unsubscribed' | 'admin_manual';
      actor?: string;
      before?: string;
      limit?: number;
    } = {},
  ) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) {
      if (v === undefined || v === '') continue;
      sp.set(k, String(v));
    }
    return crmFetch<{
      ok: true;
      entries: ContactBlockAuditEntry[];
      next_before: string | null;
    }>(`/api/v1/crm/contact-blocks/audit?${sp.toString()}`);
  },
  getLlmStats: (
    q: {
      group_by?: 'module' | 'template_id' | 'model' | 'status' | 'hour' | 'day';
      module?: string;
      template_id?: string;
      model?: string;
      status?: 'ok' | 'error' | 'rate_limited';
      since?: string;
      until?: string;
      limit?: number;
    } = {},
  ) => {
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) {
      if (v === undefined || v === '') continue;
      sp.set(k, String(v));
    }
    const s = sp.toString();
    return crmFetch<LlmStatsResponse>(`/api/v1/crm/llm-calls/stats${s ? `?${s}` : ''}`);
  },
  outboundSend: (payload: {
    request: {
      draft_id: string;
      customer_id: string;
      message_id: string;
      to: Array<{ email: string; name?: string }>;
      subject: string;
      body_text: string;
      body_html?: string;
      body_language: string;
      triggered_by: string;
      template_id?: string;
      trace_id?: string;
    };
    trace_id?: string;
  }) =>
    crmFetch<{ ok: boolean; event_id?: string; delivered?: number; total?: number }>(
      '/api/v1/crm/outbound/send',
      { method: 'POST', body: payload },
    ),
  reviewDecision: (payload: {
    decision_id: string;
    by: string;
    action: 'accept' | 'reject' | 'dismiss' | 'edit' | 'note' | 'override_priority' | 'transfer';
    edited_result?: Record<string, unknown>;
    note?: string;
    priority?: 'A' | 'B' | 'C';
    transferred_to?: string;
    trace_id?: string | null;
  }) =>
    crmFetch<{ ok: boolean; already_applied?: boolean; new_review?: string }>(
      '/api/v1/agent_decision/review',
      { method: 'POST', body: payload },
    ),
  // ---- C2 供应侧只读 ----
  listSuppliers: (
    q: {
      limit?: number;
      skip?: number;
      q?: string;
      status?: string;
      category?: string;
      cooperation_status?: string;
      cooperation_level?: string;
      product_line?: string;
    } = {},
  ) => crmFetch<SkipListResponse<SupplierEntry>>(`/api/v1/crm/suppliers${buildQuery(q)}`),
  /** 供应商体检结果(周检产出);only_issues=只看有问题的 */
  supplierHealth: (onlyIssues = false) =>
    crmFetch<{
      ok: boolean;
      entries: SupplierHealthEntry[];
      summary: {
        total: number;
        red: number;
        amber: number;
        gray: number;
        ok: number;
        checked_at: string | null;
      };
    }>(`/api/v1/crm/supplier-health${onlyIssues ? '?only_issues=1' : ''}`),
  /** 手动重跑体检(补完资料想马上看结果);不推飞书周报 */
  supplierHealthRun: () =>
    crmFetch<{ ok: boolean; running?: boolean }>('/api/v1/crm/supplier-health/run', {
      method: 'POST',
      body: {},
    }),
  /** 搜索框:人话 → 结构化筛选条件(秒回,解析失败自动退回关键词) */
  searchParse: (q: string, scope: 'suppliers' | 'products' = 'suppliers') =>
    crmFetch<{ ok: boolean; result: ParsedSearch }>('/api/v1/crm/search/parse', {
      method: 'POST',
      body: { q, scope },
    }),
  /** 问 Sitebox:agent 多步查库后用中文回答(慢,几十秒) */
  ask: (q: string, operatorName?: string) =>
    crmFetch<{ ok: boolean; reply?: string; error?: string; elapsed_ms?: number }>(
      '/api/v1/crm/ask',
      { method: 'POST', body: operatorName ? { q, operator_name: operatorName } : { q } },
    ),
  /** 同步总状态:有没有正在跑的那轮 + 上一轮结果 */
  supplierSyncStatus: () =>
    crmFetch<{
      ok: boolean;
      enabled: boolean;
      summary: SupplierSyncSummary | null;
      error: string | null;
      running_run_id: string | null;
    }>('/api/v1/crm/suppliers/sync-feishu'),
  /** 起一轮同步。立刻返回 run_id,进度和日志去 supplierSyncRunGet 轮询 */
  supplierSyncRun: () =>
    crmFetch<{ ok: boolean; running?: boolean; started?: boolean; run_id: string | null }>(
      '/api/v1/crm/suppliers/sync-feishu',
      { method: 'POST', body: {} },
    ),
  /** 某一轮的进度 + 日志 + 结果 */
  supplierSyncRunGet: (id: string) =>
    crmFetch<{ ok: boolean; run: SupplierSyncRunEntry }>(
      `/api/v1/crm/suppliers/sync-feishu/runs/${encodeURIComponent(id)}`,
    ),
  /** 最近几轮同步日志(列表不含 logs 明细) */
  supplierSyncRuns: (limit = 20) =>
    crmFetch<{ ok: boolean; runs: SupplierSyncRunEntry[] }>(
      `/api/v1/crm/suppliers/sync-feishu/runs?limit=${limit}`,
    ),
  /** 单个供应商档案 + 名下产品数(详情/编辑页用) */
  supplierGet: (id: string) =>
    crmFetch<{ ok: boolean; supplier: SupplierEntry; product_count: number }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}`,
    ),
  // ---- 供应商资料记忆（工厂 AI 回复时参考）----
  supplierMemoryGet: (id: string) =>
    crmFetch<{ ok: true } & SupplierMemoryOverview>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/memory`,
    ),
  supplierMemoryUpload: (
    id: string,
    body: { file_name?: string; content_base64?: string; text?: string },
  ) =>
    crmFetch<{ ok: true; proposal_id: string }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/memory/sources`,
      { method: 'POST', body },
    ),
  supplierMemoryApply: (id: string, pid: string, decisions: Record<string, 'accept' | 'reject'>) =>
    crmFetch<{ ok: true; applied: number; rev: number }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/memory/proposals/${encodeURIComponent(pid)}/apply`,
      { method: 'POST', body: { decisions } },
    ),
  supplierMemoryDiscard: (id: string, pid: string) =>
    crmFetch<{ ok: true }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/memory/proposals/${encodeURIComponent(pid)}/discard`,
      { method: 'POST', body: {} },
    ),
  supplierMemoryRerun: (id: string, pid: string) =>
    crmFetch<{ ok: true }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/memory/proposals/${encodeURIComponent(pid)}/rerun`,
      { method: 'POST', body: {} },
    ),
  /** 测试回复：扮买家问，后端用跟真实询盘同一条路起草，不落库不发出 */
  supplierMemorySimulate: (id: string, messages: Array<{ role: 'buyer' | 'ai'; text: string }>) =>
    crmFetch<SupplierMemorySimulateResult>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/memory/simulate`,
      { method: 'POST', body: { messages } },
    ),
  supplierMemoryRollback: (id: string, vid: string) =>
    crmFetch<{ ok: true; rev: number }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/memory/versions/${encodeURIComponent(vid)}/rollback`,
      { method: 'POST', body: {} },
    ),
  /** 保存供应商档案。业务员手填的事实字段,有意识覆盖(填空即清空);SKU 不迁移。 */
  // ---- 订单与采购单拆分（10-23）----
  listCustomerPurchaseInquiries: (customerId: string) =>
    crmFetch<{ ok: true; entries: PurchaseInquiryEntry[] }>(
      `/api/v1/crm/customers/${encodeURIComponent(customerId)}/purchase-inquiries`,
    ),
  /** 发采购询价 → 飞书审批。失败时 body.message 是给人看的 */
  createPurchaseInquiry: (customerId: string, input: PurchaseInquiryInput) =>
    crmFetch<{ ok: true; inquiry_no: string; inquiry_id: string; instance_code: string }>(
      `/api/v1/crm/customers/${encodeURIComponent(customerId)}/purchase-inquiries`,
      { method: 'POST', body: input },
    ),
  markPurchaseInquiryQuoted: (inquiryId: string, quoteId: string) =>
    crmFetch<{ ok: true; changed: boolean }>(
      `/api/v1/crm/purchase-inquiries/${encodeURIComponent(inquiryId)}/quoted`,
      { method: 'POST', body: { quote_id: quoteId } },
    ),
  /** 确认线下收款 → 飞书审批（财务）。凭证 base64，10MB 以内 */
  requestOfflinePayment: (
    quoteId: string,
    input: {
      amount: number;
      method: '电汇' | '西联' | 'PayPal' | '其他';
      received_at: string;
      note: string | null;
      file: { name: string; content_base64: string };
    },
  ) =>
    crmFetch<{ ok: true; instance_code: string }>(
      `/api/v1/crm/quotes/${encodeURIComponent(quoteId)}/offline-payment`,
      { method: 'POST', body: input },
    ),
  listPurchaseOrders: (
    q: { order_id?: string; supplier_id?: string; status?: string; limit?: number } = {},
  ) =>
    crmFetch<{ ok: true; entries: PurchaseOrderEntry[] }>(
      `/api/v1/crm/purchase-orders${buildQuery(q)}`,
    ),
  getPurchaseOrder: (id: string) =>
    crmFetch<{ ok: true; purchase_order: PurchaseOrderEntry; events: OrderEventEntry[] }>(
      `/api/v1/crm/purchase-orders/${encodeURIComponent(id)}`,
    ),
  changePurchaseOrderStatus: (id: string, status: string, note?: string | null) =>
    crmFetch<{ ok: true; status: string; label: string; order_advanced_to: string | null }>(
      `/api/v1/crm/purchase-orders/${encodeURIComponent(id)}/status`,
      { method: 'POST', body: { status, note: note ?? null } },
    ),
  patchPurchaseOrder: (
    id: string,
    patch: {
      tracking_no?: string | null;
      carrier?: string | null;
      delivered_to?: string | null;
      due_at?: string | null;
      notes?: string | null;
    },
  ) =>
    crmFetch<{ ok: true; purchase_order: PurchaseOrderEntry }>(
      `/api/v1/crm/purchase-orders/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  resendPurchaseApproval: (orderId: string) =>
    crmFetch<{ ok: true; instance_code: string }>(
      `/api/v1/crm/orders/${encodeURIComponent(orderId)}/purchase-approval`,
      { method: 'POST', body: {} },
    ),
  listFeishuApprovals: (q: { processed_status?: string; kind?: string; limit?: number } = {}) =>
    crmFetch<{ ok: true; entries: FeishuApprovalEntry[] }>(
      `/api/v1/crm/feishu-approvals${buildQuery(q)}`,
    ),
  replayFeishuApproval: (instanceCode: string) =>
    crmFetch<{ ok: true; processed_status: string; error: string | null }>(
      `/api/v1/crm/feishu-approvals/${encodeURIComponent(instanceCode)}/replay`,
      { method: 'POST', body: {} },
    ),
  supplierDeleteImpact: (id: string) =>
    crmFetch<{ ok: true; impact: SupplierDeleteImpact; can_delete: boolean }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/delete-impact`,
    ),
  /** 提交删除 → 飞书「供应商删除」审批。有进行中的采购单 / 审批没接上时回错误，message 是给人看的 */
  requestSupplierDelete: (id: string, reason: string) =>
    crmFetch<{ ok: true; instance_code: string; impact: SupplierDeleteImpact }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}/delete-request`,
      { method: 'POST', body: { reason } },
    ),
  updateSupplier: (id: string, patch: SupplierPatch) =>
    crmFetch<{ ok: boolean; supplier: SupplierEntry }>(
      `/api/v1/crm/suppliers/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  /**
   * 「AI 读聊天记录填一填」。业务员主动点才跑 —— 这是花钱的调用，
   * 不进任何默认流程。成本落 llm_calls，按 template_id 查得到。
   */
  quoteAiFill: (customerId: string) =>
    crmFetch<QuoteAiFillResult>(
      `/api/v1/crm/customers/${encodeURIComponent(customerId)}/quote-ai-fill`,
      // crmFetch 自己会 JSON.stringify(opts.body)，这里再 stringify 一次
      // body 就成了 `"{}"` 这么个 JSON 字符串，koa 的 bodyparser 只收对象和数组 → 400
      { method: 'POST', body: {} },
    ),

  /** 报价单总览（管理员看全部；租户账号只看自己的，作用域在后端并进查询） */
  listQuotes: (
    q: {
      q?: string;
      status?: string;
      customer_id?: string;
      /** 'true' = 连改价的历史版本一起列；默认只列没被取代的那一版 */
      include_superseded?: string;
      limit?: number;
      skip?: number;
    } = {},
  ) =>
    crmFetch<{ ok: true; entries: QuoteListEntry[]; total: number }>(
      `/api/v1/crm/quotes${buildQuery(q)}`,
    ),

  /**
   * 「生成报价单」先看到的那一屏候选货：这个客户报过价的 + 对话里提过且货盘里真有的。
   * 后端不调模型，正则取候选串再拿去货盘精确匹配，匹配不上的一个都不出现。
   */
  listQuoteCandidates: (customerId: string) =>
    crmFetch<{
      ok: true;
      candidates: QuoteCandidateEntry[];
      scanned_messages: number;
      site: QuoteSiteHint | null;
    }>(`/api/v1/crm/customers/${encodeURIComponent(customerId)}/quote-candidates`),

  listProducts: (
    q: {
      limit?: number;
      skip?: number;
      q?: string;
      supplier_id?: string;
      category?: string;
      status?: string;
      batch_id?: string;
      published?: 'any' | 'none';
      published_site?: string;
      price_min?: string | number;
      price_max?: string | number;
      /** 只看「在售但货盘站上看不见」的那批 */
      catalog_state?: 'hidden';
      /** 只看缺图 / 缺价的（判据借货盘投影的 hidden_reason） */
      missing?: 'image' | 'price';
      include_archived?: boolean | string;
    } = {},
  ) =>
    crmFetch<
      SkipListResponse<ProductEntry> & { catalog_sites?: string[]; catalog_capped?: boolean }
    >(`/api/v1/crm/products${buildQuery(q)}`),
  /** 当前筛选下全部产品的 global_sku(不分页)——批量上架"全选筛选结果"用 */
  listProductAllSkus: (
    q: {
      q?: string;
      supplier_id?: string;
      category?: string;
      status?: string;
      batch_id?: string;
      published?: 'any' | 'none';
      published_site?: string;
      price_min?: string | number;
      price_max?: string | number;
      catalog_state?: 'hidden';
      missing?: 'image' | 'price';
      include_archived?: boolean | string;
    } = {},
  ) =>
    crmFetch<{ ok: true; global_skus: string[]; total: number; capped: boolean }>(
      `/api/v1/crm/products/all-skus${buildQuery(q)}`,
    ),
  /** 最近 C6 导入批次(产品页"按批次筛选"下拉) */
  listMigrationBatches: () =>
    crmFetch<{
      ok: true;
      batches: {
        batch_id: string;
        label: string;
        count: number;
        created_at: string;
        status: string;
      }[];
    }>(`/api/v1/crm/migration/batches`),
  /**
   * 单 SKU 详情。globalSku 格式 `<supplier_code>/<factory_sku>`,
   * 内部拆成两段做 URL 拼接(各自 encodeURIComponent)。
   */
  productGet: (globalSku: string) => {
    const [sc, ...skuParts] = globalSku.split('/');
    const fs = skuParts.join('/'); // 兼容 factory_sku 里恰好有 `/` 的边角情况
    if (!sc || !fs) throw new Error(`invalid global_sku: ${globalSku}`);
    return crmFetch<{
      ok: boolean;
      product: ProductEntry;
      supplier: SupplierEntry | null;
    }>(`/api/v1/crm/products/${encodeURIComponent(sc)}/${encodeURIComponent(fs)}`);
  },
  /**
   * 员工手动编辑 SKU。注意:**直接覆盖**(包括 null 显式清空),与 ingest 的"只填空"
   * 语义相反。supplier_id / factory_sku / global_sku / status 不可改。
   */
  /**
   * 批量下架 / 恢复上架。
   *
   * `status: 'archived'` = 从站上拿下来；`'active'` = 放回去。
   * 返回里 `kept_published` 是「已上架到别的盒子站、这次没动」的那几条，
   * **必须逐条给业务员看** —— 它们是真的没被下架，当成功计数就是骗人。
   * `catalog_synced=false` 表示库里改了但站上投影没重算成功，站上还没生效。
   */
  archiveProducts: (body: {
    global_skus: string[];
    status: 'archived' | 'active';
    force_published?: boolean;
  }) =>
    crmFetch<{
      ok: true;
      changed: number;
      kept_published: string[];
      not_found: string[];
      catalog_synced?: boolean;
      catalog_error?: string | null;
    }>('/api/v1/crm/products/archive', {
      method: 'POST',
      // 传对象，**不要自己 JSON.stringify** —— crmFetch 会 stringify 一次。
      // 自己先 stringify 一遍就是双重编码：服务端收到的是一个 JSON 字符串，
      // zod 拿它当 object 校验直接 400。2026-09-14 线上点下架就是这么炸的。
      body,
    }),

  /**
   * 人工调这几条货在站上排第几。`weight: null` = 清空人工值，回到自动判断。
   *
   * 跟 `archiveProducts` 一样传对象、**不要自己 stringify**（crmFetch 会 stringify 一次）。
   */
  setProductSortWeight: (body: { global_skus: string[]; weight: number | null }) =>
    crmFetch<{
      ok: true;
      changed: number;
      weight: number | null;
      not_found: string[];
      catalog_synced?: boolean;
      catalog_error?: string | null;
    }>('/api/v1/crm/products/sort-weight', { method: 'POST', body }),

  /** 批量改售价:ratio=按增价比(%)、price=统一售价(元)、reset=回到站级倍率 */
  setProductSellPrice: (
    body:
      | { global_skus: string[]; mode: 'ratio' | 'price'; value: number }
      | { global_skus: string[]; mode: 'reset' },
  ) =>
    crmFetch<{
      ok: true;
      changed: number;
      not_found: string[];
      no_cost: string[];
      catalog_synced?: boolean;
      catalog_error?: string | null;
    }>('/api/v1/crm/products/sell-price', { method: 'POST', body }),

  productUpdate: (globalSku: string, patch: ProductPatch) => {
    const [sc, ...skuParts] = globalSku.split('/');
    const fs = skuParts.join('/');
    if (!sc || !fs) throw new Error(`invalid global_sku: ${globalSku}`);
    return crmFetch<{ ok: boolean; product: ProductEntry }>(
      `/api/v1/crm/products/${encodeURIComponent(sc)}/${encodeURIComponent(fs)}`,
      { method: 'PATCH', body: patch },
    );
  },
  listCrafts: (
    q: {
      limit?: number;
      skip?: number;
      q?: string;
      status?: string;
    } = {},
  ) => crmFetch<SkipListResponse<CraftEntry>>(`/api/v1/crm/crafts${buildQuery(q)}`),
  catalogSummary: () => crmFetch<CatalogSummary>('/api/v1/crm/catalog/summary'),
  dashboardSummary: () => crmFetch<DashboardSummary>('/api/v1/crm/dashboard/summary'),
  factoryTemplates: () =>
    crmFetch<{ ok: boolean; templates: FactoryTemplate[]; frequency_days: number }>(
      '/api/v1/crm/factory-outreach/templates',
    ),
  factoryCategories: () =>
    crmFetch<{
      ok: boolean;
      categories: string[];
      items: Array<{ name: string; count: number }>;
      materials: FactoryTag[];
      products: FactoryTag[];
    }>('/api/v1/crm/factory-outreach/categories'),
  factoryTargets: (body: {
    categories?: string[];
    materials?: string[];
    products?: string[];
    include_contacted?: boolean;
  }) =>
    crmFetch<{
      ok: boolean;
      rows: FactoryTargetRow[];
      counts: {
        total: number;
        eligible: number;
        no_email: number;
        blocked: number;
        recent: number;
      };
      phone_tasks: FactoryPhoneTask[];
      bulk_ready: boolean;
      tag_counts: { materials: Record<string, number>; products: Record<string, number> };
    }>('/api/v1/crm/factory-outreach/targets', { method: 'POST', body }),
  factoryPreview: (body: { template_id: string; supplier_id: string }) =>
    crmFetch<{ ok: boolean; to: string | null; subject: string; body: string }>(
      '/api/v1/crm/factory-outreach/preview',
      { method: 'POST', body },
    ),
  createFactoryCampaign: (body: {
    name: string;
    template_id: string;
    supplier_ids: string[];
    daily_cap?: number;
    phone_tasks?: FactoryPhoneTask[];
  }) =>
    crmFetch<{ ok: boolean; id: string; queued: number }>(
      '/api/v1/crm/factory-outreach/campaigns',
      {
        method: 'POST',
        body,
      },
    ),
  factoryCampaigns: () =>
    crmFetch<{ ok: boolean; items: FactoryCampaign[] }>('/api/v1/crm/factory-outreach/campaigns'),
  factoryCampaign: (id: string) =>
    crmFetch<{ ok: boolean; campaign: FactoryCampaign }>(
      `/api/v1/crm/factory-outreach/campaigns/${encodeURIComponent(id)}`,
    ),
  factoryCampaignOp: (id: string, op: 'pause' | 'resume' | 'cancel') =>
    crmFetch<{ ok: boolean }>(
      `/api/v1/crm/factory-outreach/campaigns/${encodeURIComponent(id)}/${op}`,
      { method: 'POST', body: {} },
    ),
  factoryReplies: (q: { class?: string; follow?: string } = {}) =>
    crmFetch<{
      ok: boolean;
      items: FactoryReply[];
      counts: Record<string, number>;
      follow_open: number;
    }>(`/api/v1/crm/factory-outreach/replies${buildQuery({ ...q })}`),
  factoryReplyClass: (id: string, cls: FactoryReplyClass) =>
    crmFetch<{ ok: boolean }>(
      `/api/v1/crm/factory-outreach/replies/${encodeURIComponent(id)}/class`,
      { method: 'POST', body: { class: cls } },
    ),
  factoryReplyFacts: (
    id: string,
    body: { min_order_qty?: number; lead_time_days?: number; capacity_monthly_pcs?: number },
  ) =>
    crmFetch<{ ok: boolean; changes?: string[]; error?: string }>(
      `/api/v1/crm/factory-outreach/replies/${encodeURIComponent(id)}/facts`,
      { method: 'POST', body },
    ),
  factoryReplyFollow: (id: string, body: { status: 'done' | 'open'; note?: string }) =>
    crmFetch<{ ok: boolean }>(
      `/api/v1/crm/factory-outreach/replies/${encodeURIComponent(id)}/follow`,
      { method: 'POST', body },
    ),
  outreachFunnel: (q: OutreachFunnelQuery = {}) =>
    crmFetch<OutreachFunnelResponse>(`/api/v1/crm/outreach/funnel${buildQuery({ ...q })}`),
  outreachSends: (q: OutreachFunnelQuery & { stage?: string; limit?: number } = {}) =>
    crmFetch<{ ok: boolean; total: number; items: OutreachSendRow[] }>(
      `/api/v1/crm/outreach/sends${buildQuery({ ...q })}`,
    ),
  setOutreachOutcome: (
    draftId: string,
    body: { outcome: OutreachOutcome | null; reason?: string | null },
  ) =>
    crmFetch<{ ok: boolean }>(`/api/v1/crm/outreach/sends/${encodeURIComponent(draftId)}/outcome`, {
      method: 'POST',
      body,
    }),
  outboundChannelStatus: () =>
    crmFetch<OutboundChannelsReport>('/api/v1/crm/outbound/channels/status'),
  inboundChannelStatus: () =>
    crmFetch<InboundChannelsReport>('/api/v1/crm/inbound/channels/status'),
  // ---- C5 撮合 ----
  matchSupplier: (body: { inquiry_text: string; customer_id?: string; max_candidates?: number }) =>
    crmFetch<MatchSupplierResponse>('/api/v1/crm/match-supplier', {
      method: 'POST',
      body,
    }),
  /**
   * SSE 版撮合 — Playground 调用,从点击瞬间起每段都 fire 事件。
   *
   * 返回 Promise<void>,在流结束时 resolve;onEvent 拿到每条 SSE 帧的 JSON payload。
   * abortSignal 取消 → fetch reader 中断 + Promise reject('aborted')。
   */
  matchSupplierStream: async (
    body: { inquiry_text: string; customer_id?: string; max_candidates?: number },
    onEvent: (evt: MatchStreamEvent) => void,
    abortSignal?: AbortSignal,
  ): Promise<void> => {
    const initHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Crm-Module': 'admin',
    };
    const init: RequestInit = {
      method: 'POST',
      credentials: 'include',
      headers: initHeaders,
      body: JSON.stringify(body),
    };
    if (abortSignal) init.signal = abortSignal;

    console.log('[matchSupplierStream] about to fetch SSE endpoint');
    const t0 = performance.now();
    const res = await fetch('/api/v1/crm/match-supplier/stream', init);

    console.log(
      '[matchSupplierStream] fetch returned HTTP',
      res.status,
      'in',
      Math.round(performance.now() - t0),
      'ms · has body=',
      !!res.body,
    );
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');

      console.error('[matchSupplierStream] response NOT ok:', res.status, text.slice(0, 500));
      throw new CrmApiError(res.status, text, `match stream ${res.status}: ${text.slice(0, 200)}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let chunkCount = 0;
    let eventCount = 0;
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        console.log(
          '[matchSupplierStream] reader done · chunks=',
          chunkCount,
          'events=',
          eventCount,
        );
        break;
      }
      chunkCount++;
      buf += decoder.decode(value, { stream: true });
      while (true) {
        const idx = buf.indexOf('\n\n');
        if (idx < 0) break;
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          const json = line.slice(6);
          try {
            const evt = JSON.parse(json) as MatchStreamEvent;
            eventCount++;
            onEvent(evt);
          } catch (err) {
            console.warn(
              '[matchSupplierStream] frame parse failed:',
              err,
              'json=',
              json.slice(0, 200),
            );
          }
        }
      }
    }
  },
  // ---- 导入时没认出来的列（字段候选）----
  fieldCandidatesList: (q: { status?: string; limit?: number } = {}) =>
    crmFetch<{
      ok: true;
      pending_count: number;
      items: FieldCandidateEntry[];
    }>(`/api/v1/crm/field-candidates${buildQuery(q)}`),
  fieldCandidateIgnore: (id: string, body: { note?: string } = {}) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/field-candidates/${encodeURIComponent(id)}/ignore`, {
      method: 'POST',
      body,
    }),
  fieldCandidateRestore: (id: string) =>
    crmFetch<{ ok: true }>(`/api/v1/crm/field-candidates/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    }),
  // ---- C6 供应商迁移 (agent-runner) ----
  migrationList: (q: { limit?: number } = {}) =>
    crmFetch<{ ok: true; items: MigrationListEntry[]; total: number }>(
      `/api/v1/crm/migration/list${buildQuery(q)}`,
    ),
  migrationStart: (body: { folder_token: string; by_user: string; dry_run?: boolean }) =>
    crmFetch<{ batch_id: string; task_id: string; status: 'queued' }>(
      '/api/v1/crm/migration/start',
      { method: 'POST', body },
    ),
  migrationGet: (
    id: string,
    q: { limit?: number; page?: number; page_size?: number; status?: string; q?: string } = {},
  ) =>
    crmFetch<{
      batch: MigrationBatchEntry;
      rows: MigrationRowEntry[];
      /** 这一批一共多少行 */
      total_rows: number;
      /** 当前筛选/搜索下多少行（翻页按它算） */
      filtered_total?: number;
      page?: number;
      page_size?: number;
      /** 行状态 → 行数，筛选按钮上的数字 */
      status_counts?: Record<string, number>;
      /** open_id → {name, avatar_url}，含 by_user/committed_by/reverted_by */
      user_profiles?: Record<string, { name: string | null; avatar_url: string | null }>;
    }>(`/api/v1/crm/migration/${encodeURIComponent(id)}${buildQuery(q)}`),
  migrationCommit: (id: string, body: { by_user: string }) =>
    crmFetch<MigrationCommitSummary>(`/api/v1/crm/migration/${encodeURIComponent(id)}/commit`, {
      method: 'POST',
      body,
    }),
  migrationRevert: (id: string, body: { by_user: string }) =>
    crmFetch<MigrationRevertSummary>(`/api/v1/crm/migration/${encodeURIComponent(id)}/revert`, {
      method: 'POST',
      body,
    }),
  migrationCancel: (id: string) =>
    crmFetch<{ ok: true; task_canceled: boolean; reason?: string }>(
      `/api/v1/crm/migration/${encodeURIComponent(id)}/cancel`,
      { method: 'POST', body: {} },
    ),
  migrationDelete: (id: string) =>
    crmFetch<{ ok: true; deleted_batch: string; deleted_task: string | null }>(
      `/api/v1/crm/migration/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
  // ---- Agent task 通用 ----
  agentTasksList: (q: { status?: AgentTaskStatus; task_type?: string; limit?: number } = {}) =>
    crmFetch<{ tasks: AgentTaskEntry[]; total: number }>(
      `/api/v1/agent/tasks${buildQuery(q as ListQuery)}`,
    ),
  agentTaskGet: (id: string) =>
    crmFetch<{ task: AgentTaskEntry; logs: AgentTaskLogEntry[] }>(
      `/api/v1/agent/tasks/${encodeURIComponent(id)}`,
    ),
  agentTaskLogs: (id: string, q: { limit?: number } = {}) =>
    crmFetch<{ task_id: string; logs: AgentTaskLogEntry[] }>(
      `/api/v1/agent/tasks/${encodeURIComponent(id)}/logs${buildQuery(q as ListQuery)}`,
    ),

  /* ─────────────  询盘生命周期 admin 操作（Stage 5, 2026-05-27）  ───────────── */
  getInboxLifecycle: (customer_event_id: string) =>
    crmFetch<{ ok: true; lifecycle: InboxLifecycleSnapshot }>(
      `/api/v1/crm/inbox-lifecycle/${encodeURIComponent(customer_event_id)}`,
    ),
  forceResumeInboxLifecycle: (customer_event_id: string, payload: ForceResumePayload = {}) =>
    crmFetch<{ ok: true; lifecycle: InboxLifecycleSnapshot }>(
      `/api/v1/crm/inbox-lifecycle/${encodeURIComponent(customer_event_id)}/force-resume`,
      { method: 'POST', body: payload },
    ),
  markPermanentInboxLifecycle: (customer_event_id: string, reason: string) =>
    crmFetch<{ ok: true; lifecycle: InboxLifecycleSnapshot }>(
      `/api/v1/crm/inbox-lifecycle/${encodeURIComponent(customer_event_id)}/mark-permanent`,
      { method: 'POST', body: { reason } },
    ),
  overrideStageInboxLifecycle: (
    customer_event_id: string,
    payload: { to_stage: LifecycleStage; reason: string },
  ) =>
    crmFetch<{ ok: true; lifecycle: InboxLifecycleSnapshot }>(
      `/api/v1/crm/inbox-lifecycle/${encodeURIComponent(customer_event_id)}/override-stage`,
      { method: 'POST', body: payload },
    ),
};

/** 询盘生命周期 stage 枚举（admin UI 选项 + force-resume 入参；与后端 LIFECYCLE_STAGES 同步） */
export const LIFECYCLE_STAGES = [
  'pending_c7',
  'pending_f3',
  'pending_f5_push',
  'claimed',
  'drafted',
  'sent',
  'closed',
  'permanent_failed',
  'paused',
] as const;
export type LifecycleStage = (typeof LIFECYCLE_STAGES)[number];

export interface InboxLifecycleSnapshot {
  inbox_message_id: string;
  channel_msg_id: string | null;
  lifecycle_stage: LifecycleStage | null;
  retry_count: number;
  next_retry_at: string | null;
  last_error: { kind: string; message: string; at: string } | null;
  stage_history: Array<{
    stage: string;
    at: string;
    ok: boolean;
    error: string | null;
  }>;
}

export interface ForceResumePayload {
  to_stage?: LifecycleStage;
  reason?: string;
}

// ---- C6 migration types ----
export type MigrationBatchStatus =
  | 'pending'
  | 'downloading'
  | 'parsing'
  | 'er'
  | 'dry-run-done'
  | 'committed'
  | 'reverted'
  | 'failed';

export interface MigrationBatchCounts {
  total: number;
  parsed: number;
  matched: number;
  pending_merge: number;
  created: number;
  /** 缺 title 等关键字段被挡在入库门外的行;commit 不写,业务员补全后再走 */
  incomplete: number;
  /**
   * 款号不合规被挡下的行（目前只有「款号带中文」一条规则）。
   * 跟 `incomplete` 分开数：原因不一样，业务员的下一步动作也不一样。
   */
  invalid_sku?: number;
  written: number;
  failed: number;
  /** commit 后回填:本批实际入库的不重复 SKU 数(written 是行数,两者可能差很多) */
  sku_total?: number | null;
  /** commit 后回填:因完全相同被合并掉的行数 */
  rows_merged_duplicates?: number | null;
}

/** GET /api/v1/crm/migration/list 单行:task ⨝ batch ⨝ rows 聚合(供应商名/SKU 数) */
export interface FieldCandidateEntry {
  id: string;
  /** 归一后的列名（聚合键） */
  column_key: string;
  /** 首次见到的原始列名 */
  display_name: string;
  /** 见过的所有写法 */
  variants: string[];
  type_hint: 'text' | 'number' | 'enum' | 'url' | 'unknown';
  status: 'pending' | 'merged' | 'promoted' | 'ignored';
  supplier_count: number;
  file_count: number;
  rows_filled_total: number;
  suppliers: string[];
  sample_values: string[];
  last_file: string | null;
  /** 'no-sku-column' = 整张表被判定没有 SKU 列跳过，这一列只是那张表的其中一列 */
  last_reason: 'unmapped-column' | 'no-sku-column';
  first_seen_at: string | null;
  last_seen_at: string | null;
  resolved_to: string | null;
  note: string | null;
}

export interface MigrationListEntry {
  task_id: string;
  batch_id: string | null;
  status: AgentTaskStatus;
  attempts: number;
  by_user: string;
  by_user_profile?: { open_id: string; name: string | null; avatar_url: string | null } | null;
  created_at: string;
  finished_at: string | null;
  folder_token: string | null;
  /** batch.folder_summary 里的文件名列表(任务没跑到抓取阶段则为空) */
  file_names: string[];
  files: number;
  /** rows 聚合出的供应商/工厂名(去重) */
  suppliers: string[];
  /** rows 聚合出的解析 SKU 总数 */
  sku_count: number;
  /** counts.written:>0 表示已入库,可跳产品页按批次筛 */
  written: number;
  batch_status: string | null;
  /**
   * 这批写进去多少款（按行记录里的款号算、去重后）。
   * 「货不记批次」——产品上没有批次号，只能从行记录反查。
   */
  batch_skus: number | null;
  /** 其中现在站上没图的 / 没价的。算不出来（批次太多 / 没接投影）时是 null，页面写「—」 */
  batch_no_image: number | null;
  batch_no_price: number | null;
}

export interface MigrationBatchEntry {
  _id: string;
  folder_token: string;
  by_user: string;
  task_id: string | null;
  status: MigrationBatchStatus;
  dry_run: boolean;
  counts: MigrationBatchCounts;
  committed_by: string | null;
  reverted_by: string | null;
  committed_at: string | null;
  reverted_at: string | null;
  error: string | null;
  folder_summary: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export type MigrationRowStatus =
  | 'pending'
  | 'parsed'
  | 'er-matched'
  | 'er-pending-merge'
  | 'er-created'
  | 'er-incomplete'
  | 'written'
  | 'failed'
  | 'skipped';

export interface MigrationRowMatchResult {
  action: 'matched' | 'pending-merge' | 'created' | null;
  supplier_id: string | null;
  supplier_code: string | null;
  confidence: number | null;
  rule: string | null;
  candidates?: unknown;
}

export interface MigrationRowEntry {
  _id: string;
  batch_id: string;
  row_index: number;
  source: 'xlsx' | 'csv' | 'feishu-base' | 'manual';
  source_ref: string | null;
  status: MigrationRowStatus;
  raw: Record<string, unknown> | null;
  parsed: Record<string, unknown> | null;
  match_result: MigrationRowMatchResult;
  supplier_id_written: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface MigrationCommitSummary {
  batch_id: string;
  /** 处理成功的行数 */
  written: number;
  skipped_pending_merge: number;
  skipped_failed: number;
  errors: Array<{ row_id: string; reason: string }>;
  /** 本批实际入库的不重复 SKU 数 */
  sku_total: number;
  rows_merged_duplicates: number;
  products_archived_not_in_batch: number;
  products_kept_published: string[];
}

export interface MigrationRevertSummary {
  batch_id: string;
  status: 'reverted';
  reverted: number;
}

// ---- Agent task 通用 types ----
export type AgentTaskStatus = 'queued' | 'running' | 'done' | 'failed' | 'canceled';

export interface UserProfileLite {
  open_id: string;
  name: string | null;
  avatar_url: string | null;
}

export interface AgentTaskEntry {
  _id: string;
  task_type: string;
  args: Record<string, unknown>;
  status: AgentTaskStatus;
  by_user: string;
  /** 后端 enrich 的发起人头像昵称（open_id 命中 users/salespeople 时才有） */
  by_user_profile?: UserProfileLite;
  attempts: number;
  created_at: string;
  started_at: string | null;
  heartbeat_at: string | null;
  finished_at: string | null;
  next_retry_at: string | null;
  error: string | null;
  result?: unknown;
}

export type AgentTaskLogType = 'stdout' | 'stderr' | 'milestone' | 'error' | 'system';

export interface AgentTaskLogEntry {
  task_id: string;
  ts: string;
  type: AgentTaskLogType;
  payload: unknown;
}

// ---- C5 撮合 types ----
export interface ParsedInquiry {
  products: string[];
  materials: string[];
  crafts: string[];
  categories_zh: string[];
  title_keywords_zh: string[];
  constraints: {
    moq_max?: number | null;
    moq_min?: number | null;
    price_max_cny?: number | null;
    lead_time_days_max?: number | null;
    market?: string | null;
    certifications?: string[];
    export_required?: boolean | null;
  };
  intent: 'first-order' | 'reorder' | 'sampling' | 'pricing-inquiry' | 'rfq' | 'unknown';
  confidence: number;
  unparsed_notes: string[];
}

export interface RankedCandidate {
  index: number;
  score: number;
  reason: string;
  gaps: string[];
  product_id: string;
  supplier_id: string;
  supplier_name: string;
  supplier_country: string | null;
  factory_sku: string;
  title: string | null;
  category: string | null;
  price_cny: number | null;
  moq: number | null;
  lead_time_days: number | null;
  export_capable: boolean | null;
  current_score: number | null;
  url_1688: string | null;
}

export interface LlmCallTrace {
  template_id: string;
  template_version: string;
  model: string;
  variables: Record<string, string>;
  raw_output: string;
  parsed: unknown;
  usage: {
    input_tokens: number;
    output_tokens: number;
    cache_read_input_tokens?: number;
    cache_creation_input_tokens?: number;
  };
  cost_usd: number;
  cached: boolean;
  latency_ms: number;
}

export interface Phase2Attempt {
  attempt_index: number;
  relaxed_key: string;
  product_match: Record<string, unknown>;
  supplier_match: Record<string, unknown>;
  row_count: number;
}

export interface Phase2Trace {
  final_pipeline: Array<Record<string, unknown>>;
  attempts: Phase2Attempt[];
  relaxation_steps: string[];
}

export interface MatchTrace {
  phase1: LlmCallTrace | null;
  phase2: Phase2Trace | null;
  phase3: LlmCallTrace | null;
}

export type MatchSupplierResponse =
  | {
      ok: true;
      trace_id: string;
      phase_taken: 'p3';
      parsed_inquiry: ParsedInquiry;
      phase2: { candidate_count: number; relaxation_steps: string[] };
      candidates: RankedCandidate[];
      agent_decision_id: string | null;
      timings_ms: { phase1: number; phase2: number; phase3: number; total: number };
      trace: MatchTrace;
    }
  | {
      ok: false;
      trace_id: string;
      code: 'phase1_low_confidence' | 'phase2_empty' | 'phase1_parse_error' | 'phase3_parse_error';
      message: string;
      parsed_inquiry?: ParsedInquiry;
      phase2_candidate_count?: number;
      trace?: MatchTrace;
    };

/**
 * SSE 帧 payload 类型 —— 与 c5-match-service.ts MatchEvent + c5-match.ts 路由帧对齐。
 */
export type MatchStreamEvent =
  | { type: 'ready'; ts: number }
  | { type: 'heartbeat'; ts: number }
  | { type: 'phase1_start'; ts: number }
  | {
      type: 'phase1_done';
      ts: number;
      latency_ms: number;
      trace: LlmCallTrace;
      parsed: ParsedInquiry;
    }
  | { type: 'phase2_start'; ts: number }
  | {
      type: 'phase2_done';
      ts: number;
      latency_ms: number;
      trace: Phase2Trace;
      candidate_count: number;
    }
  | { type: 'phase3_start'; ts: number }
  | {
      type: 'phase3_done';
      ts: number;
      latency_ms: number;
      trace: LlmCallTrace;
      candidates: RankedCandidate[];
    }
  | {
      type: 'fail';
      ts: number;
      code: 'phase1_low_confidence' | 'phase2_empty' | 'phase1_parse_error' | 'phase3_parse_error';
      message: string;
    }
  | { type: 'fatal'; ts: number; message: string }
  | { type: 'final'; ts: number; result: MatchSupplierResponse };

export interface PendingAssignment {
  customer_id: string;
  display_name: string | null;
  current_tenant_id: string | null;
  last_seen_at: string;
  channel: string;
  site_slug: string | null;
  inbound_to_label: string | null;
  reason: string | null;
}

/** 社媒监听：一个被盯着的账号 */
export interface SocialWatchEntry {
  id: string;
  tenant_id: string;
  tenant_name: string | null;
  platform: 'tiktok';
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  enabled: boolean;
  poll_interval_min: number;
  max_videos: number;
  last_polled_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  last_run: {
    id: string;
    status: string;
    egress_ip: string | null;
    finished_at: string | null;
    comments_new: number;
  } | null;
  active_run: { id: string; status: string } | null;
  egress: { tag: string | null; ready: boolean };
  comments: { total: number; fresh: number; intent: number };
  /** 各处理档的评论数（口径同工厂端「等你回复」） */
  stages: Record<SocialStage, number>;
  /** 工厂端这一行显示的状态 */
  portal_state: PortalSocialAccount['state'];
}

/**
 * 评论处理到哪一步（crm-engine services/social-stage.ts 同一份口径）：
 * pending 等工厂回 · replied 已回 · dismissed 工厂点了不用回 · expired 超 30 天没回 ·
 * no_reply 模型判不用回 · unjudged 还没判
 */
export type SocialStage = 'pending' | 'replied' | 'dismissed' | 'expired' | 'no_reply' | 'unjudged';

export interface SocialOverview {
  ok: true;
  watches: number;
  tenants: number;
  comments_total: number;
  comments_7d: number;
  stages: Record<SocialStage, number>;
  /** 该回的里面回了多少（0–1）；还没有该回的评论时 null */
  reply_rate: number | null;
  avg_reply_hours: number | null;
  runs_24h: { ok: number; failed: number };
}

/** 社媒监听：一次抓取的过程 */
export interface SocialRunEntry {
  id: string;
  status: 'queued' | 'running' | 'ok' | 'failed';
  trigger: 'schedule' | 'manual';
  egress_tag: string | null;
  egress_ip: string | null;
  videos_total: number;
  videos_done: number;
  comments_seen: number;
  comments_new: number;
  logs: Array<{ at: string; msg: string }>;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
}

/** 社媒监听：收到的一条评论 */
export interface SocialCommentEntry {
  id: string;
  watch_id: string;
  platform: 'tiktok';
  comment_id: string;
  video_id: string;
  video_url: string | null;
  video_desc: string | null;
  text: string;
  lang: string | null;
  author_handle: string | null;
  author_name: string | null;
  author_avatar: string | null;
  like_count: number;
  reply_count: number;
  posted_at: string | null;
  first_seen_at: string;
  intent_hint: boolean;
  intent_keywords: string[];
  status: 'new' | 'seen' | 'lead' | 'ignored';
  tenant_id: string;
  tenant_name: string | null;
  text_zh?: string | null;
  needs_reply?: boolean;
  reply_intent?: string | null;
  reply_suggestion?: string | null;
  reply_suggestion_zh?: string | null;
  intent_checked_at?: string | null;
  owner_replied?: boolean;
  owner_replied_at?: string | null;
  reply_opened_at?: string | null;
  reply_dismissed_at?: string | null;
  by_owner?: boolean;
}

/** 工厂端「社媒线索」：一个绑上来的 TikTok 账号 */
export type SocialPlatformKey = 'tiktok' | 'youtube' | 'facebook' | 'instagram';

export interface PortalSocialAccount {
  id: string;
  platform: SocialPlatformKey;
  /** 账号在平台上的主页 */
  profile_url?: string | null;
  /** Facebook / Instagram 的号跟着「连接 Facebook」走，在渠道页断开 */
  managed_by_connection?: boolean;
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  state: 'watching' | 'checking' | 'line_pending' | 'paused' | 'not_found' | 'delayed';
  interval_min: number;
  last_checked_at: string | null;
}

/** 工厂端「社媒线索」：一条评论 */
export interface PortalSocialComment {
  id: string;
  platform?: SocialPlatformKey;
  /** Facebook / Instagram 能在页面上直接回 */
  can_reply_here?: boolean;
  account_handle: string | null;
  video_url: string | null;
  video_desc: string | null;
  text: string;
  text_zh: string | null;
  lang: string | null;
  author_handle: string | null;
  author_name: string | null;
  author_avatar: string | null;
  posted_at: string | null;
  first_seen_at: string;
  /** 模型判的意图（受控词表）；不用回的是 null */
  intent: string | null;
  /** 模型起草的回复，点「去回复」时复制 */
  reply_suggestion: string | null;
  reply_suggestion_zh: string | null;
  needs_reply: boolean;
  /** 账号主人已经在这条下面回过 */
  replied: boolean;
  /** 工厂点过「去回复」 */
  opened: boolean;
}

// ---- 供应商资料记忆 ----
export interface SupplierMemoryLine {
  line_id: string;
  text: string;
  source_name: string;
  added_at: string;
}
export interface SupplierMemoryFile {
  path: string;
  title: string;
  hook: string;
  lines: SupplierMemoryLine[];
  markdown: string;
}
export interface SupplierMemoryProposalItem {
  item_id: string;
  kind: 'add' | 'conflict';
  file: string;
  file_title: string | null;
  text: string;
  quote: string;
  old_line_id: string | null;
  old_text: string | null;
  old_source_name: string | null;
}
export interface SupplierMemoryProposal {
  _id: string;
  status: 'running' | 'pending' | 'applied' | 'discarded' | 'failed';
  entry: 'import' | 'upload';
  source_name: string;
  items?: SupplierMemoryProposalItem[];
  dropped?: Array<{ text: string; reason: string }>;
  error: string | null;
  created_at: string;
  decided_at: string | null;
}
export interface SupplierMemoryVersion {
  _id: string;
  rev: number;
  reason: 'apply' | 'rollback';
  note: string;
  created_at: string;
}
export interface SupplierMemoryOverview {
  rev: number;
  index: string;
  files: SupplierMemoryFile[];
  open_proposals: SupplierMemoryProposal[];
  recent_proposals: SupplierMemoryProposal[];
  versions: SupplierMemoryVersion[];
}
export interface SupplierMemorySimulateResult {
  ok: true;
  reply: string;
  tool_calls: Array<{ name: string; summary: string; ok: boolean }>;
  memory_files: number;
  brand_name: string | null;
  latency_ms: number;
  context_messages: number;
  context_dropped: number;
  limits: {
    message_chars: number;
    context_messages: number;
    context_chars: number;
    buyer_turns: number;
  };
}

/* ── 10-22 工厂端 CRM 类型 ── */

export interface NewCustomerInput {
  display_name?: string;
  organization?: string;
  country?: string;
  emails?: string[];
  phones?: string[];
  wa_ids?: string[];
  stage?: string;
  note?: string;
  assign_to_me?: boolean;
}

export interface CustomerImportRow {
  row: number;
  name: string;
  action: 'create' | 'update' | 'same_as_row' | 'error';
  message?: string;
  existing_id?: string;
  same_as_row?: number;
}

export type CustomerImportField =
  | 'display_name'
  | 'organization'
  | 'country'
  | 'emails'
  | 'phones'
  | 'wa_ids'
  | 'stage'
  | 'note';

export interface CustomerImportMapping {
  header_row: number;
  columns: Record<string, CustomerImportField>;
}

export interface CustomerImportPlan {
  ok: true;
  mapping: CustomerImportMapping;
  /** ai = AI 认的；rules = AI 用不了按列名认的；manual = 人改过 */
  mapping_source: 'ai' | 'rules' | 'manual';
  notice: string | null;
  fields: Array<{ key: CustomerImportField; label: string }>;
  columns: Array<{
    col: number;
    name: string;
    samples: string[];
    field: CustomerImportField | null;
  }>;
  max_rows: number;
  total: number;
  create: number;
  update: number;
  same_as_row: number;
  error: number;
  results: CustomerImportRow[];
}

export type SampleStatusKey = 'preparing' | 'sent' | 'received' | 'feedback' | 'closed';

export interface CustomerSampleInput {
  items: string;
  quantity?: string;
  status?: SampleStatusKey;
  carrier?: string;
  tracking_no?: string;
  fee_note?: string;
  feedback?: string;
  note?: string;
}

export interface CustomerSampleView {
  id: string;
  customer_id: string;
  items: string;
  quantity: string | null;
  status: SampleStatusKey;
  status_label: string;
  carrier: string | null;
  tracking_no: string | null;
  sent_at: string | null;
  fee_note: string | null;
  feedback: string | null;
  note: string | null;
  created_by_name: string | null;
  created_at: string | null;
}

/** 「渠道」页：每个渠道的绑定情况（crm-engine routes/channels.ts） */
/** 工厂端「渠道 → 邮箱」一条绑定（11-03） */
export interface PortalMailbox {
  id: string;
  address: string;
  /** hosted = 网站域名开通；mailbox = 授权码绑的企业邮箱；other = 平台配的 */
  kind: 'hosted' | 'mailbox' | 'other';
  via: string;
  site: string | null;
  display_name: string | null;
  ok: boolean;
  last_error: string | null;
  last_message_at: string | null;
  ingested_count: number;
  filtered_count: number;
  can_remove: boolean;
  can_reauth: boolean;
}

export interface PortalMailboxes {
  ok: true;
  self_serve: boolean;
  sites: Array<{ slug: string; name: string; domain: string; has_own_mail: boolean }>;
  items: PortalMailbox[];
}

export interface PortalMailboxProvider {
  id: string;
  label: string;
  supported: boolean;
  message: string | null;
  guide: string[];
  needs_servers: boolean;
}

export interface PortalChannels {
  ok: true;
  self_operated: boolean;
  channels: {
    site: { bound: boolean; items: Array<{ slug: string; name: string; domain: string | null }> };
    whatsapp: {
      bound: boolean;
      connected: number;
      total: number;
      official: Array<{ number: string; label: string }>;
    };
    email: { bound: boolean; items: Array<{ address: string; site: string | null; ok: boolean }> };
    tiktok: { bound: boolean; items: PortalChannelWatch[] };
    youtube: { available: boolean; bound: boolean; items: PortalChannelWatch[] };
    meta: {
      self_serve: boolean;
      configured: boolean;
      bound: boolean;
      pages: Array<{
        connection_id: string;
        page_id: string;
        name: string;
        ig_username: string | null;
        has_instagram: boolean;
        receiving: boolean;
        last_event_at: string | null;
        connected_by_name: string | null;
      }>;
    };
  };
}

export interface PortalChannelWatch {
  id: string;
  name: string;
  handle: string;
  enabled: boolean;
  last_ok_at: string | null;
}

/** 大后台「各厂渠道」 */
export interface ChannelsOverview {
  ok: true;
  meta: {
    app_id: string | null;
    app_secret_set: boolean;
    /** 商务版 Facebook 登录的配置编号（没配走 scope） */
    login_config_id: string | null;
    verify_token_set: boolean;
    callback_url: string;
    webhook_urls: { page: string; ig: string };
    subscriptions: Array<{
      object: string;
      callback_url: string;
      active: boolean;
      fields: string[];
    }> | null;
    subscriptions_error: string | null;
  };
  youtube: { api_key_set: boolean };
  tenants: Array<{
    id: string;
    name: string;
    tier: string;
    self_operated: boolean;
    /** 工厂详情页的「连接 Facebook」开关 */
    meta_connect_enabled: boolean;
    sites: string[];
    whatsapp: { total: number; connected: number };
    social: Array<{
      id: string;
      platform: SocialPlatformKey;
      name: string;
      enabled: boolean;
      last_ok_at: string | null;
      last_error: string | null;
    }>;
    meta_pages: Array<{
      page_id: string;
      name: string;
      ig_username: string | null;
      subscribed: boolean;
      subscribe_error: string | null;
      last_event_at: string | null;
    }>;
  }>;
}
